import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaPlus, FaTag, FaTimes, FaEye, FaTrash, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { categoryService, mechanicGarageService, productService, userService } from '../../../../services/apiService';
import api, { getApiErrorMessage } from '../../../../utils/api';
import {
  dealCategoryRequiresStructuredAttributes,
  formatDealEligibilityError,
  getDealStructuredAttributeGaps,
  isProductDealReady
} from '../../../../utils/dealProductEligibility';
import { toast } from 'react-toastify';
import {
  getGarageDealServiceOptions,
  getFullCatalogServiceLines,
  findServiceLineById,
  groupServiceLinesByCluster,
  isPackageDealBlockedForTier,
} from '../../../../utils/garageServiceCatalog';
import { normalizeGarageTier } from '../../../../constants/garageServiceClusters';

const DEAL_CATEGORIES = [
  { id: 'all', label: 'All Deals', icon: '🔥' },
  { id: 'flash', label: 'Flash Sale', icon: '⚡' },
  { id: 'brand', label: 'Brand Campaign', icon: '🏢' },
  { id: 'clearance', label: 'Clearance', icon: '🏷️' },
  { id: 'combo', label: 'Combo Bundle', icon: '📦' },
  { id: 'coupon', label: 'Coupon', icon: '🎟️' },
  { id: 'seller_own', label: 'Seller Own Deal', icon: '🛍️' },
  { id: 'garage_service', label: 'Garage Service', icon: '🛠️' },
];

const STORAGE_KEY = 'adminDeals';
const DEAL_CREATOR_POLICY = {
  flash: ['superadmin', 'admin'],
  brand: ['superadmin', 'admin', 'vendor', 'used_vendor'],
  clearance: ['superadmin', 'admin', 'vendor', 'used_vendor'],
  combo: ['superadmin', 'admin'],
  coupon: ['superadmin', 'admin', 'garage'],
  seller_own: ['superadmin', 'admin', 'vendor', 'used_vendor', 'garage'],
  garage_service: ['garage', 'admin', 'superadmin'],
};

/** Deal pool commitment % is admin-set only for these types (not Flash / Brand). */
const DEAL_TYPES_WITH_POOL_COMMITMENT = ['clearance', 'combo', 'seller_own'];

const STANDARD_DEAL_STATUS_OPTIONS = [
  { value: 'Draft', label: 'Draft' },
  { value: 'Active', label: 'Active' },
  { value: 'Scheduled', label: 'Scheduled' }
];

const combineDateTimeLocal = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const time = timeStr || '00:00';
  const d = new Date(`${dateStr}T${time}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const normalizeRole = (role) => {
  const r = String(role || '').toLowerCase();
  if (r === 'buyer') return 'customer';
  if (r === 'new seller') return 'vendor';
  if (r === 'used seller') return 'used_vendor';
  if (r === 'mechanic') return 'mechanics';
  if (r === 'garages') return 'garage';
  return r;
};

const readStoredUserRole = () => {
  try {
    const fromStorage = normalizeRole(localStorage.getItem('userRole'));
    if (fromStorage) return fromStorage;
    const raw = localStorage.getItem('user');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return normalizeRole(parsed?.role);
  } catch {
    return normalizeRole(localStorage.getItem('userRole'));
  }
};

const GARAGE_APPROVAL_DISCOUNT_THRESHOLD = 40;
const GARAGE_APPROVAL_LOCKED_BASE = 1000;

function getGarageFormDiscountPct(form) {
  if (!form) return 0;
  if (form.garageDiscountType === 'amount') {
    const amt = Math.max(0, Number(form.garageDiscountAmount) || 0);
    return GARAGE_APPROVAL_LOCKED_BASE > 0
      ? Number(((amt / GARAGE_APPROVAL_LOCKED_BASE) * 100).toFixed(2))
      : 0;
  }
  return Math.max(0, Math.min(100, Number(form.discountPct) || 0));
}

function garageDiscountNeedsAdminApproval(discountPct) {
  return Number(discountPct) > GARAGE_APPROVAL_DISCOUNT_THRESHOLD;
}

function isDealApprovalPending(deal) {
  const status = String(deal?.approvalStatus || '');
  return status === 'pending_admin' || status === 'pending_superadmin';
}

function isHomepagePlacementPending(deal) {
  const hp = deal?.homepagePlacement || {};
  return !!hp.requested && String(hp.status || '') === 'pending_admin';
}

function formatHomepagePlacementLabel(deal) {
  const hp = deal?.homepagePlacement || {};
  if (!hp.requested) return 'Not requested';
  if (hp.status === 'approved') return `Approved — ${hp.paymentTerms || 'terms set'}`;
  if (hp.status === 'rejected') return 'Rejected';
  if (hp.status === 'pending_admin') return 'Pending admin (homepage)';
  return hp.status || '—';
}

function canGarageActivateDeal(deal) {
  const approval = String(deal?.approvalStatus || 'not_required');
  if (isDealApprovalPending(deal) || approval === 'rejected') return false;
  return approval === 'approved' || approval === 'not_required';
}

const GARAGE_ACTIVE_REQUIRES_ADMIN_MSG =
  'You need admin permission to make this deal active. Deals above 40% stay Draft until admin approves.';

const normalizeDealForUi = (deal) => {
  const raw = deal || {};
  return {
    ...raw,
    id: raw.id || raw._id || ''
  };
};

const parseFlatCategoriesResponse = (result) => {
  if (result?.categories && Array.isArray(result.categories)) return result.categories;
  if (Array.isArray(result)) return result;
  if (result?.data?.categories && Array.isArray(result.data.categories)) return result.data.categories;
  if (result?.data && Array.isArray(result.data)) return result.data;
  return [];
};

function RequiredMark() {
  return <span className="text-red-500 font-bold ml-0.5" aria-hidden="true">*</span>;
}

function GarageCitiesMultiDropdown({ cities = [], value = [], onChange, loading = false, disabled = false, invalid = false }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const selected = Array.isArray(value) ? value : [];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleCity = (city) => {
    const next = selected.includes(city)
      ? selected.filter((c) => c !== city)
      : [...selected, city];
    onChange(next);
  };

  const triggerLabel = loading
    ? 'Loading cities…'
    : cities.length === 0
      ? 'No cities with registered garages'
      : selected.length === 0
        ? 'Select cities'
        : selected.length === 1
          ? selected[0]
          : `${selected.length} cities selected`;

  return (
    <div ref={containerRef} className="relative mt-2">
      <button
        type="button"
        disabled={disabled || loading || cities.length === 0}
        onClick={() => setOpen((prev) => !prev)}
        data-create-field="garageServiceCities"
        className={`w-full px-4 py-2.5 rounded-xl bg-white border text-left flex items-center justify-between gap-2 disabled:opacity-60 ${
          invalid ? 'deal-field-invalid border-2 border-[#FF0000]' : 'border-slate-200'
        }`}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={`truncate text-sm ${selected.length ? 'text-slate-900' : 'text-slate-500'}`}>
          {triggerLabel}
        </span>
        <span className={`text-slate-400 text-xs shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>
      {open && cities.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg py-1"
          role="listbox"
          aria-multiselectable="true"
        >
          {cities.map((city) => {
            const checked = selected.includes(city);
            return (
              <label
                key={city}
                role="option"
                aria-selected={checked}
                className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer text-sm ${
                  checked ? 'bg-indigo-50 text-indigo-900' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <input
                  type="checkbox"
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  checked={checked}
                  onChange={() => toggleCity(city)}
                />
                <span>{city}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

const Deals = () => {
  const navigate = useNavigate();
  const [currentRole, setCurrentRole] = useState(() => readStoredUserRole());
  const [activeCategory, setActiveCategory] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all'); // all | pending
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewDeal, setViewDeal] = useState(null);
  const [deleteConfirmDeal, setDeleteConfirmDeal] = useState(null);
  const [deals, setDeals] = useState([]);
  const [currentUserId, setCurrentUserId] = useState('');
  const [participateLoadingByDealId, setParticipateLoadingByDealId] = useState({});
  const [deleteLoadingByDealId, setDeleteLoadingByDealId] = useState({});
  const [reviewLoadingByDealId, setReviewLoadingByDealId] = useState({});
  const [participatedDealIds, setParticipatedDealIds] = useState({});
  const [dealActionFeedback, setDealActionFeedback] = useState({ type: '', message: '' });
  const [stockByProductId, setStockByProductId] = useState({});
  const [rolesOptions, setRolesOptions] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesDropdownOpen, setRolesDropdownOpen] = useState(false);
  const [rolesAudience, setRolesAudience] = useState('selected'); // 'all' | 'selected'
  const [productOptions, setProductOptions] = useState([]);
  const [productOptionsLoading, setProductOptionsLoading] = useState(false);
  const [catalogCategoryOptions, setCatalogCategoryOptions] = useState([]);
  const [catalogCategoriesLoading, setCatalogCategoriesLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState('');
  const [createFieldErrors, setCreateFieldErrors] = useState([]);
  const [createFieldErrorMessages, setCreateFieldErrorMessages] = useState({});
  const shouldFocusCreateFieldErrorRef = useRef(false);
  const [createDealLoading, setCreateDealLoading] = useState(false);
  const [garageStatusLoadingByDealId, setGarageStatusLoadingByDealId] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [dealsConfig, setDealsConfig] = useState({
    flashRules: {
      holdMinutes: 3,
      maxUnitsPerUser: 1,
      cancelCooldownSeconds: 45
    },
    sellerFloor: {
      minPricePctOfLockedBase: 0
    },
    clearanceRules: null
  });
  const [clearanceDrafts, setClearanceDrafts] = useState([]);
  const [clearanceDraftsLoading, setClearanceDraftsLoading] = useState(false);
  const [clearanceScanLoading, setClearanceScanLoading] = useState(false);
  const [clearanceActionLoading, setClearanceActionLoading] = useState({});
  const [clearanceAcceptDiscount, setClearanceAcceptDiscount] = useState({});
  const [pendingClearanceDraftId, setPendingClearanceDraftId] = useState(null);
  const [comboQualificationReport, setComboQualificationReport] = useState(null);
  const [comboFailures, setComboFailures] = useState([]);
  const [comboFailuresLoading, setComboFailuresLoading] = useState(false);
  const [comboPreviewLoading, setComboPreviewLoading] = useState(false);
  const [comboAnchorModalDealId, setComboAnchorModalDealId] = useState('');
  const [comboAnchorContext, setComboAnchorContext] = useState(null);
  const [comboAnchorLoading, setComboAnchorLoading] = useState(false);
  const [comboAnchorSubmitting, setComboAnchorSubmitting] = useState(false);
  const [comboAnchorNewProductId, setComboAnchorNewProductId] = useState('');
  const [poolCommitmentDraft, setPoolCommitmentDraft] = useState('');
  const [poolCommitmentLoading, setPoolCommitmentLoading] = useState(true);
  const [poolCommitmentSaving, setPoolCommitmentSaving] = useState(false);
  const sellerFloorPct = Number(dealsConfig?.sellerFloor?.minPricePctOfLockedBase) || 0;
  const sellerFloorBadgeClass = sellerFloorPct > 0
    ? 'inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700'
    : 'inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700';
  const sellerFloorRowBadgeClass = sellerFloorPct > 0
    ? 'inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700'
    : 'inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700';
  const [form, setForm] = useState({
    title: '',
    category: 'flash',
    discountType: 'percent',
    discountAmount: 0,
    discountPct: 0,
    startDate: '',
    endDate: '',
    status: 'Live',
    commitmentPercentage: 20,
    eligibleRoles: [],
    couponCode: '',
    couponDiscountType: 'percent',
    couponDiscountAmount: 0,
    couponMinOrderValue: 0,
    couponMaxDiscountAmount: 0,
    couponUsageLimitPerUser: 1,
    couponFirstOrderOnly: false,
    brandBudgetTotal: 0,
    brandMaxDiscountPerOrder: 0,
    brandCampaignBrand: '',
    brandCategoryFilter: '',
    brandProductId: '',
    flashSelectionType: 'category',
    startTime: '00:00',
    endTime: '23:59',
    scheduledGoLiveDate: '',
    scheduledGoLiveTime: '09:00',
    flashFilterBrand: '',
    flashFilterCategory: '',
    flashFilterSku: '',
    flashFilterProductId: '',
    comboComponents: [{ productId: '', minQty: 1 }],
    comboBundleName: '',
    comboDiscountPct: 0,
    comboItems: [
      { productId: '' },
      { productId: '' }
    ],
    couponScopeType: 'all',
    couponScopeLabel: '',
    couponStackableWithDeals: false,
    garageRequiresVehicleNumber: false,
    garageServiceId: '',
    garageServiceCities: [],
    garageServiceAreaPincodes: [],
    garagePincodeDraft: '',
    garageDiscountType: 'percent',
    garageDiscountAmount: 0,
    productPickerBrand: '',
    productPickerCategory: '',
    sellerOwnSellerId: '',
    requestHomepagePlacement: false
  });
  const [homepageTermsByDealId, setHomepageTermsByDealId] = useState({});
  const [homepageActionLoadingByDealId, setHomepageActionLoadingByDealId] = useState({});
  const [brandPreview, setBrandPreview] = useState(null);
  const [brandPreviewLoading, setBrandPreviewLoading] = useState(false);
  const [garageServiceOptions, setGarageServiceOptions] = useState([]);
  const [garageProfileTier, setGarageProfileTier] = useState('');
  const [garagePincodeError, setGaragePincodeError] = useState('');
  const [sellerOptions, setSellerOptions] = useState([]);
  const [sellerOptionsLoading, setSellerOptionsLoading] = useState(false);
  const [sellerOwnOtpStep, setSellerOwnOtpStep] = useState('idle');
  const [sellerOwnOtp, setSellerOwnOtp] = useState('');
  const [sellerOwnOtpHint, setSellerOwnOtpHint] = useState('');
  const [sellerOwnOtpSending, setSellerOwnOtpSending] = useState(false);
  const [registeredGarageCities, setRegisteredGarageCities] = useState([]);
  const [registeredGarageCitiesLoading, setRegisteredGarageCitiesLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      // Prefer backend deals if available, fallback to localStorage.
      try {
        const role = normalizeRole(currentRole);
        const dealsEndpoint = (role === 'superadmin' || role === 'admin') ? '/admin/deals' : '/deals';
        const res = await api.get(dealsEndpoint);
        const list = res.data?.data?.deals || res.data?.deals || [];
        if (Array.isArray(list)) {
          setDeals(list.map(normalizeDealForUi));
          return;
        }
      } catch (_) {
        // ignore and fallback
      }
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setDeals(parsed.map(normalizeDealForUi));
      } catch (e) {
        // ignore
      }
    };
    load();
  }, [currentRole]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(deals));
    } catch (e) {
      // ignore
    }
  }, [deals]);

  const counts = useMemo(() => {
    const byCat = { all: deals.length };
    for (const c of DEAL_CATEGORIES) {
      if (c.id === 'all') continue;
      byCat[c.id] = deals.filter((d) => d.category === c.id).length;
    }
    return byCat;
  }, [deals]);

  const isComboAnchorPaused = (d) =>
    String(d?.category) === 'combo' && !!d?.comboConfig?.anchorExhaustion?.active;

  const pausedComboDeals = useMemo(
    () => (Array.isArray(deals) ? deals : []).filter(isComboAnchorPaused),
    [deals]
  );

  const filteredDeals = useMemo(() => {
    let list = Array.isArray(deals) ? deals : [];
    if (activeCategory !== 'all') {
      list = list.filter((d) => d.category === activeCategory);
    }
    if (approvalFilter === 'pending') {
      list = list.filter((d) => d.approvalStatus === 'pending_admin' || d.approvalStatus === 'pending_superadmin');
    }
    return list;
  }, [activeCategory, approvalFilter, deals]);

  const creatableCategories = useMemo(() => {
    const role = normalizeRole(currentRole);
    const platformStaff = role === 'superadmin' || role === 'admin';
    return DEAL_CATEGORIES.filter((c) => {
      if (c.id === 'all') return false;
      if (platformStaff) return true;
      return (DEAL_CREATOR_POLICY[c.id] || []).includes(role);
    });
  }, [currentRole]);
  const canCreateDeals = creatableCategories.length > 0;

  const brandOptions = useMemo(() => {
    const set = new Set();
    (productOptions || []).forEach((p) => {
      const b = String(p?.brand || '').trim();
      if (b) set.add(b);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [productOptions]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    (productOptions || []).forEach((p) => {
      const c = String(p?.category || '').trim();
      if (c) set.add(c);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [productOptions]);

  const couponScopeCategoryOptions = useMemo(() => {
    if (catalogCategoryOptions.length > 0) return catalogCategoryOptions;
    return categoryOptions;
  }, [catalogCategoryOptions, categoryOptions]);

  const isFlashDealForm = form.category === 'flash';
  const isBrandDealForm = form.category === 'brand';
  const isComboDealForm = form.category === 'combo';
  const isCouponDealForm = form.category === 'coupon';
  const isGarageServiceDealForm = form.category === 'garage_service';
  const isAdminGarageCampaignForm =
    isGarageServiceDealForm &&
    (normalizeRole(currentRole) === 'admin' || normalizeRole(currentRole) === 'superadmin');
  const isClearanceDealForm = form.category === 'clearance';
  const isSellerOwnDealForm = form.category === 'seller_own';
  /** Vendor-facing product deals: seller own + clearance share one form layout. */
  const isVendorProductDealForm = isSellerOwnDealForm || isClearanceDealForm;
  const showPoolCommitment = DEAL_TYPES_WITH_POOL_COMMITMENT.includes(form.category) && !isComboDealForm;
  const garageFormDiscountPct = useMemo(
    () => (isGarageServiceDealForm ? getGarageFormDiscountPct(form) : 0),
    // getGarageFormDiscountPct only reads discountPct, garageDiscountAmount, garageDiscountType
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isGarageServiceDealForm, form.discountPct, form.garageDiscountAmount, form.garageDiscountType]
  );
  const garageFormNeedsApproval = garageDiscountNeedsAdminApproval(garageFormDiscountPct);

  useEffect(() => {
    if (!isGarageServiceDealForm || !garageFormNeedsApproval || isAdminGarageCampaignForm) return;
    if (form.status === 'Active') {
      setForm((p) => ({ ...p, status: 'Draft' }));
    }
  }, [isGarageServiceDealForm, isAdminGarageCampaignForm, garageFormNeedsApproval, form.status]);

  useEffect(() => {
    if (!showCreateModal || !isAdminGarageCampaignForm) return undefined;
    let mounted = true;
    const loadGarageCities = async () => {
      setRegisteredGarageCitiesLoading(true);
      try {
        const cities = await mechanicGarageService.getRegisteredGarageCities();
        if (mounted) setRegisteredGarageCities(Array.isArray(cities) ? cities : []);
      } catch {
        if (mounted) setRegisteredGarageCities([]);
      } finally {
        if (mounted) setRegisteredGarageCitiesLoading(false);
      }
    };
    loadGarageCities();
    return () => {
      mounted = false;
    };
  }, [showCreateModal, isAdminGarageCampaignForm]);

  useEffect(() => {
    let mounted = true;
    const initUserContext = async () => {
      try {
        const rawUser = localStorage.getItem('user');
        let parsed = null;
        if (rawUser) {
          parsed = JSON.parse(rawUser);
          const uid = parsed?._id || parsed?.id;
          if (uid) setCurrentUserId(String(uid));
        }

        const roleFromStorage = normalizeRole(localStorage.getItem('userRole'));
        const inferredRole = normalizeRole(parsed?.role);
        const effectiveRole = roleFromStorage || inferredRole;
        if (effectiveRole) setCurrentRole(effectiveRole);

        if (effectiveRole !== 'garage') {
          if (effectiveRole === 'admin' || effectiveRole === 'superadmin') {
            setGarageServiceOptions(getFullCatalogServiceLines('large'));
          }
          return;
        }

        let profile = parsed;
        try {
          const profileRes = await api.get('/auth/profile');
          const profileData =
            profileRes?.data?.data?.user ||
            profileRes?.data?.user ||
            profileRes?.data?.data ||
            profileRes?.data;
          if (profileData && typeof profileData === 'object') {
            profile = profileData;
          }
        } catch (_) {
          // fallback to local profile data if profile fetch fails
        }

        if (!mounted) return;
        const gd = profile?.garageDetails || {};
        setGarageProfileTier(
          normalizeGarageTier(gd.garageTier || gd.sellerCategory || gd.companyType || '')
        );
        setGarageServiceOptions(getGarageDealServiceOptions(gd));
      } catch (_) {
        // ignore
      }
    };
    initUserContext();
    return () => {
      mounted = false;
    };
  }, []);

  const openCreate = () => {
    if (!canCreateDeals) {
      setProductError('Your role has view-only access for deals.');
      return;
    }
    setPendingClearanceDraftId(null);
    setSelectedProduct(null);
    setProductOptions([]);
    setProductError('');
    clearCreateFieldValidation();
    setComboQualificationReport(null);
    setRolesDropdownOpen(false);
    setRolesAudience('selected');
    const defaultCategory = creatableCategories[0]?.id || 'flash';
    const resolvedCategory = activeCategory === 'all' ? defaultCategory : activeCategory;
    const defaultStatus = ['flash', 'brand'].includes(resolvedCategory)
      ? 'Live'
      : resolvedCategory === 'garage_service'
        ? 'Active'
        : 'Draft';
    const defaultDiscount = resolvedCategory === 'clearance'
      ? (Number(clearanceRules.minDiscountPct) || 20)
      : 0;
    setForm({
      title: '',
      category: resolvedCategory,
      discountType: 'percent',
      discountAmount: 0,
      discountPct: defaultDiscount,
      startDate: '',
      endDate: '',
      status: defaultStatus,
      commitmentPercentage: defaultDealPoolCommitmentPct,
      eligibleRoles: [],
      couponCode: '',
      couponDiscountType: 'percent',
      couponDiscountAmount: 0,
      couponMinOrderValue: 0,
      couponMaxDiscountAmount: 0,
      couponUsageLimitPerUser: 1,
      couponFirstOrderOnly: false,
      brandBudgetTotal: 0,
      brandMaxDiscountPerOrder: 0,
      brandCampaignBrand: '',
      brandCategoryFilter: '',
      brandProductId: '',
      flashSelectionType: 'category',
      startTime: '00:00',
      endTime: '23:59',
      scheduledGoLiveDate: '',
    scheduledGoLiveTime: '09:00',
      flashFilterBrand: '',
      flashFilterCategory: '',
    flashFilterSku: '',
    flashFilterProductId: '',
    comboComponents: [{ productId: '', minQty: 1 }],
      comboBundleName: '',
      comboDiscountPct: 0,
      comboItems: [
        { productId: '' },
        { productId: '' }
      ],
      couponScopeType: 'all',
      couponScopeLabel: '',
      couponStackableWithDeals: false,
      garageRequiresVehicleNumber: false,
      garageServiceId: '',
      garageServiceCities: [],
      garageServiceAreaPincodes: [],
      garagePincodeDraft: '',
      garageDiscountType: 'percent',
      garageDiscountAmount: 0,
      productPickerBrand: '',
      productPickerCategory: '',
      sellerOwnSellerId: ''
    });
    setGaragePincodeError('');
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setPendingClearanceDraftId(null);
    clearCreateFieldValidation();
    setGaragePincodeError('');
    setSellerOwnOtpStep('idle');
    setSellerOwnOtp('');
    setSellerOwnOtpHint('');
    setSellerOwnOtpSending(false);
  };

  const resetSellerOwnOtpFlow = () => {
    setSellerOwnOtpStep('idle');
    setSellerOwnOtp('');
    setSellerOwnOtpHint('');
  };

  const sendSellerOwnDealOtp = async (sellerId) => {
    setSellerOwnOtpSending(true);
    setProductError('');
    try {
      const res = await api.post('/admin/deals/seller-own/send-otp', { sellerId });
      const data = res.data?.data || {};
      setSellerOwnOtpStep('sent');
      setSellerOwnOtp('');
      setSellerOwnOtpHint(data.mobileHint || '');
      const devOtp = data.devOtp;
      toast.success(
        devOtp
          ? `OTP sent to seller (dev code: ${devOtp})`
          : res.data?.message || 'OTP sent to seller mobile'
      );
      return true;
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to send OTP to seller');
      toast.error(message);
      setProductError(message);
      return false;
    } finally {
      setSellerOwnOtpSending(false);
    }
  };

  const GARAGE_PINCODE_LENGTH = 6;

  const normalizeGaragePincodeInput = (raw) =>
    String(raw || '').replace(/\D/g, '').slice(0, GARAGE_PINCODE_LENGTH);

  const addGaragePincode = () => {
    const digits = normalizeGaragePincodeInput(form.garagePincodeDraft);
    if (!digits) {
      setGaragePincodeError('Enter a 6-digit pincode.');
      return;
    }
    if (digits.length !== GARAGE_PINCODE_LENGTH) {
      setGaragePincodeError('Pincode must be exactly 6 digits.');
      return;
    }
    const existing = Array.isArray(form.garageServiceAreaPincodes) ? form.garageServiceAreaPincodes : [];
    if (existing.includes(digits)) {
      setGaragePincodeError('This pincode is already added.');
      return;
    }
    setGaragePincodeError('');
    setForm((p) => ({
      ...p,
      garageServiceAreaPincodes: [...existing, digits],
      garagePincodeDraft: ''
    }));
  };

  const removeGaragePincode = (pincode) => {
    setGaragePincodeError('');
    setForm((p) => ({
      ...p,
      garageServiceAreaPincodes: (Array.isArray(p.garageServiceAreaPincodes) ? p.garageServiceAreaPincodes : [])
        .filter((pin) => pin !== pincode)
    }));
  };

  const formatCurrency = (amount) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return '—';
    return `₹${n.toLocaleString('en-IN')}`;
  };

  const getProductId = (p) => p?._id || p?.id;
  const getDealId = (d) => String(d?._id || d?.id || '');
  const getProductName = (p) => p?.name || p?.title || p?.productName || 'Untitled product';
  const getProductPrice = (p) => p?.price ?? p?.salePrice ?? p?.mrp ?? null;
  const formatProductListingLabel = (p) => {
    const name = getProductName(p);
    const price = getProductPrice(p);
    const priceText = price != null ? formatCurrency(price) : '—';
    return `${name} — ${priceText}`;
  };

  const formatClearanceProductLabel = (p) => {
    const name = getProductName(p);
    const price = getProductPrice(p);
    const priceText = price != null ? formatCurrency(price) : '—';
    const m = p?.clearanceMetrics || {};
    const sellRate = m.sellRate90d ?? '—';
    const lastSale = m.lastSaleDate
      ? new Date(m.lastSaleDate).toLocaleDateString()
      : 'No recent sale';
    const vendorRef = p?.vendorId;
    const vendorLabel = isPlatformStaffRole && vendorRef
      ? ` · ${typeof vendorRef === 'object'
        ? (vendorRef.name || vendorRef.email || 'Seller')
        : 'Seller'}`
      : '';
    return `${name} — ${priceText} · Stock ${p?.stock ?? '—'} · Sell rate ${sellRate}% · Last sale ${lastSale}${vendorLabel}`;
  };

  const renderClearanceMetricsSummary = (p) => {
    const m = p?.clearanceMetrics || {};
    const windowDays = clearanceRules.windowDays ?? 90;
    return (
      <div className="mt-2 rounded-lg border border-amber-200 bg-white/80 px-3 py-2 text-[11px] text-amber-950">
        <p>
          <span className="font-semibold">90d sold:</span> {m.unitsSold90d ?? 0}
          {' · '}
          <span className="font-semibold">Ref stock:</span> {m.stockReference90d ?? p?.stock ?? '—'}
          {' · '}
          <span className="font-semibold">Sell rate:</span> {m.sellRate90d ?? 0}%
        </p>
        <p className="mt-0.5">
          <span className="font-semibold">Last sale:</span>{' '}
          {m.lastSaleDate ? new Date(m.lastSaleDate).toLocaleDateString() : 'Never / unknown'}
          {' · '}
          Eligibility window: {windowDays} days
        </p>
      </div>
    );
  };

  const getDiscountBaseForCurrentDealType = () => {
    if (isClearanceDealForm || isSellerOwnDealForm) {
      return selectedProduct ? Math.max(0, Number(getProductPrice(selectedProduct)) || 0) : 0;
    }
    if (isComboDealForm) {
      const items = Array.isArray(form.comboItems) ? form.comboItems : [];
      return items.reduce((sum, it) => {
        const id = String(it?.productId || '').trim();
        if (!id) return sum;
        const p = (productOptions || []).find((x) => String(x?._id || x?.id) === id);
        return sum + (Math.max(0, Number(getProductPrice(p)) || 0));
      }, 0);
    }
    if (isBrandDealForm) {
      return brandPreview?.anchorProduct
        ? Math.max(0, Number(getProductPrice(brandPreview.anchorProduct)) || 0)
        : 0;
    }
    if (isFlashDealForm) {
      if (form.flashSelectionType === 'brand' && form.flashFilterProductId) {
        const id = String(form.flashFilterProductId).trim();
        const p = (productOptions || []).find((x) => String(x?._id || x?.id) === id);
        return p ? Math.max(0, Number(getProductPrice(p)) || 0) : 0;
      }
      return GARAGE_APPROVAL_LOCKED_BASE;
    }
    return selectedProduct ? Math.max(0, Number(getProductPrice(selectedProduct)) || 0) : 0;
  };

  const formatGarageServiceDiscountLabel = (deal) => {
    const cfg = deal?.garageServiceConfig || {};
    const lockedBase = Math.max(0, Number(deal?.lockedBasePrice ?? GARAGE_APPROVAL_LOCKED_BASE) || 0);
    const type = String(cfg.discountType || 'percent').toLowerCase();

    const amount = type === 'amount'
      ? Math.max(0, Number(cfg.discountAmount) || 0)
      : lockedBase > 0
        ? Number(((lockedBase * Math.max(0, Number(deal?.discountPct) || 0)) / 100).toFixed(2))
        : 0;

    const pct = lockedBase > 0
      ? type === 'amount'
        ? Number(((amount / lockedBase) * 100).toFixed(2))
        : Math.max(0, Number(deal?.discountPct) || 0)
      : 0;

    if (pct > 0 && amount > 0) return `${pct}% (${formatCurrency(amount)} off)`;
    if (pct > 0) return `${pct}%`;
    if (amount > 0) return `${formatCurrency(amount)} off`;
    return '—';
  };

  const formatDealDateTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  };

  const getShownDealStatus = (deal, over = false) => {
    if (over) return 'Over';
    const rawStatus = String(deal?.status || '');
    if (rawStatus === 'Inactive' || rawStatus === 'Draft') return 'Draft';
    if (rawStatus === 'Live') return 'Active';
    return rawStatus || '—';
  };

  const formatCouponDiscountLabel = (deal) => {
    const type = String(deal?.couponConfig?.discountType || 'percent').toLowerCase();
    const cfg = deal?.couponConfig || {};
    const minOrderValue = Math.max(0, Number(cfg.minOrderValue) || 0);
    const cap = Math.max(0, Number(cfg.maxDiscountAmount) || 0);

    if (type === 'amount') {
      const amt = Math.max(0, Number(cfg.discountAmount) || 0);
      if (!amt) return '—';
      const pctApprox = minOrderValue > 0 ? Number(((amt / minOrderValue) * 100).toFixed(2)) : 0;
      return pctApprox > 0 ? `${pctApprox}% (~${formatCurrency(amt)} off)` : `${formatCurrency(amt)} off`;
    }

    const pct = Math.max(0, Number(deal?.discountPct) || 0);
    if (!pct) return '—';

    // Coupons are applied on cart subtotal, so amount is an approximation based on minOrderValue.
    const rawAmt = minOrderValue > 0 ? (minOrderValue * pct) / 100 : 0;
    const amountApprox = cap > 0 && rawAmt > 0 ? Math.min(rawAmt, cap) : rawAmt;

    if (amountApprox > 0 && cap > 0) {
      return `${pct}% (~${formatCurrency(amountApprox)} off, max ${formatCurrency(cap)})`;
    }
    if (amountApprox > 0) return `${pct}% (~${formatCurrency(amountApprox)} off)`;
    return cap > 0 ? `${pct}% (max ${formatCurrency(cap)})` : `${pct}%`;
  };

  const formatDealDiscountPctAndAmount = (deal) => {
    const pct = Math.max(0, Number(deal?.discountPct) || 0);
    const lockedBase = Math.max(0, Number(deal?.lockedBasePrice) || 0);
    const amount = lockedBase > 0 && pct > 0 ? Number(((lockedBase * pct) / 100).toFixed(2)) : 0;

    if (pct > 0 && amount > 0) return `${pct}% (${formatCurrency(amount)} off)`;
    if (pct > 0) return `${pct}%`;
    if (amount > 0) return `${formatCurrency(amount)} off`;
    return '—';
  };

  const getProductStock = (p) => {
    const n = Number(p?.stock);
    return Number.isFinite(n) ? n : null;
  };

  const flashBrandProductOptions = useMemo(() => {
    if (form.flashSelectionType !== 'brand') return [];
    const brand = String(form.flashFilterBrand || '').trim();
    if (!brand) return [];
    return (productOptions || []).filter((p) => {
      const id = getProductId(p);
      if (!id) return false;
      if (String(p?.brand || '').trim() !== brand) return false;
      const stock = getProductStock(p);
      return stock == null || stock > 0;
    });
  }, [form.flashSelectionType, form.flashFilterBrand, productOptions]);

  const brandCampaignProductOptions = useMemo(() => {
    const brand = String(form.brandCampaignBrand || '').trim();
    const category = String(form.brandCategoryFilter || '').trim();
    if (!brand || !category) return [];
    return (productOptions || []).filter((p) => {
      const id = getProductId(p);
      if (!id) return false;
      if (String(p?.brand || '').trim() !== brand) return false;
      if (String(p?.category || '').trim() !== category) return false;
      const stock = getProductStock(p);
      return Number.isFinite(stock) && stock > 0;
    });
  }, [form.brandCampaignBrand, form.brandCategoryFilter, productOptions]);

  const brandCampaignCategoryOptions = useMemo(() => {
    const brand = String(form.brandCampaignBrand || '').trim();
    if (!brand) return [];
    const set = new Set();
    (productOptions || []).forEach((p) => {
      if (String(p?.brand || '').trim() !== brand) return;
      const category = String(p?.category || '').trim();
      if (category) set.add(category);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [form.brandCampaignBrand, productOptions]);

  const hasUserParticipated = (deal, userId) => {
    if (!deal || !userId) return false;
    const participants = Array.isArray(deal.participants) ? deal.participants : [];
    return participants.some((p) => {
      const uid = p?.userId?._id || p?.userId?.id || p?.userId;
      return String(uid || '') === String(userId);
    });
  };

  const normalizedCurrentRole = useMemo(() => normalizeRole(currentRole), [currentRole]);
  const isSuperAdminRole = normalizedCurrentRole === 'superadmin';
  const isPlatformStaffRole = normalizedCurrentRole === 'superadmin' || normalizedCurrentRole === 'admin';
  const canManageParticipationRoles = isPlatformStaffRole;

  const getDiscountApprovalHint = () => {
    if (isSuperAdminRole) {
      return 'Discount above 40% requires super-admin approval before the deal goes live.';
    }
    if (normalizedCurrentRole === 'admin') {
      return 'Discount above 40% requires super-admin approval before the deal goes live.';
    }
    return 'Discount above 40% requires admin approval before the deal goes live.';
  };

  const canReviewDeal = (deal) => {
    const status = String(deal?.approvalStatus || '');
    if (status === 'pending_admin') return isPlatformStaffRole;
    if (status === 'pending_superadmin') return isSuperAdminRole;
    return false;
  };

  const formatApprovalStatusLabel = (deal) => {
    const status = String(deal?.approvalStatus || 'not_required');
    if (status === 'pending_admin') return 'Pending admin';
    if (status === 'pending_superadmin') return 'Pending super-admin';
    if (status === 'approved') return 'Approved';
    if (status === 'rejected') return 'Rejected';
    return 'Not required';
  };

  const canDeleteDeal = (deal) => {
    if (!deal) return false;
    if (isPlatformStaffRole) return true;
    const creatorId = String(deal?.createdBy?._id || deal?.createdBy?.id || deal?.createdBy || '');
    const isOwner = !!creatorId && !!currentUserId && creatorId === String(currentUserId);
    if (normalizedCurrentRole === 'garage') {
      const cat = String(deal?.category || '').toLowerCase();
      return isOwner && ['garage_service', 'seller_own', 'coupon'].includes(cat);
    }
    return isOwner;
  };

  const isDealCreator = (deal) => {
    const creatorId = String(deal?.createdBy?._id || deal?.createdBy?.id || deal?.createdBy || '');
    return !!creatorId && !!currentUserId && creatorId === String(currentUserId);
  };

  /** Participate = join pool / campaign; hide for creators and deal types that do not use it. */
  const canShowParticipateButton = (deal) => {
    if (!deal) return false;
    const role = normalizedCurrentRole;
    if (role === 'superadmin' || role === 'admin') return false;

    const category = String(deal.category || '').toLowerCase();
    if (category === 'coupon' || category === 'combo') return false;
    if (category === 'seller_own' || category === 'clearance') return false;
    if (isDealCreator(deal)) return false;

    if (category === 'garage_service') {
      const isPlatformGarageCampaign = !!deal?.garageServiceConfig?.isPlatformCampaign;
      if (!isPlatformGarageCampaign) return false;
      return role === 'garage';
    }

    const sellerRoles = ['vendor', 'used_vendor'];
    if (!sellerRoles.includes(role)) return false;

    const eligible = Array.isArray(deal.eligibleRoles)
      ? deal.eligibleRoles.map((r) => normalizeRole(r))
      : [];
    if (eligible.length > 0 && !eligible.includes(role)) return false;

    return true;
  };

  const fetchProductById = async (id) => {
    if (!id) return null;
    if (isSuperAdminRole) {
      const res = await productService.getAdminProductById(id);
      return res?.product || res?.data?.product || res;
    }
    const res = await productService.getProductById(id);
    return res?.product || res?.data?.product || res;
  };

  const refreshDealStocks = async (dealList) => {
    const ids = Array.from(
      new Set((dealList || []).map((d) => d?.productId).filter(Boolean))
    );
    if (ids.length === 0) return;

    try {
      const results = await Promise.allSettled(
        ids.map(async (id) => {
          const product = await fetchProductById(id);
          const stock = getProductStock(product);
          return { id, stock };
        })
      );

      const nextStockMap = {};
      for (const r of results) {
        if (r.status !== 'fulfilled') continue;
        nextStockMap[r.value.id] = r.value.stock;
      }

      setStockByProductId((prev) => ({ ...prev, ...nextStockMap }));

      // If stock is over, mark deal as Over automatically.
      setDeals((prevDeals) => {
        let changed = false;
        const updated = prevDeals.map((d) => {
          if (String(d.category) === 'garage_service') return d;
          const stock = nextStockMap[d.productId];
          if (stock === 0 || stock < 0) {
            if (d.status !== 'Over') {
              changed = true;
              return { ...d, status: 'Over' };
            }
          }
          return d;
        });
        return changed ? updated : prevDeals;
      });
    } catch (e) {
      // ignore background refresh errors
    }
  };

  useEffect(() => {
    refreshDealStocks(deals);
    const interval = setInterval(() => refreshDealStocks(deals), 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deals.length]);

  useEffect(() => {
    if (!isPlatformStaffRole) {
      setRolesOptions([]);
      return;
    }
    const loadRoles = async () => {
      setRolesLoading(true);
      try {
        const res = await api.get('/users/roles');
        const roles = res.data?.data?.roles || [];
        setRolesOptions(Array.isArray(roles) ? roles : []);
      } catch (e) {
        setRolesOptions([]);
      } finally {
        setRolesLoading(false);
      }
    };
    loadRoles();
  }, [isPlatformStaffRole]);

  useEffect(() => {
    if (!isPlatformStaffRole) return;
    const loadDealsConfig = async () => {
      try {
        const res = await api.get('/admin/deals/config');
        const data = res.data?.data || {};
        setDealsConfig((prev) => ({
          ...prev,
          ...data,
          flashRules: { ...prev.flashRules, ...(data.flashRules || {}) },
          sellerFloor: { ...prev.sellerFloor, ...(data.sellerFloor || {}) },
          clearanceRules: data.clearanceRules || prev.clearanceRules
        }));
      } catch (_) {
        // keep defaults
      }
    };
    loadDealsConfig();
  }, [isPlatformStaffRole]);

  useEffect(() => {
    const loadDealPoolCommitmentConfig = async () => {
      setPoolCommitmentLoading(true);
      try {
        const endpoint = isPlatformStaffRole
          ? '/admin/deals/clearance/config'
          : '/deals/clearance/config';
        const res = await api.get(endpoint);
        const rules = res.data?.data?.clearanceRules;
        if (rules) {
          setDealsConfig((prev) => ({
            ...prev,
            clearanceRules: { ...(prev.clearanceRules || {}), ...rules }
          }));
        }
      } catch (_) {
        // keep existing config
      } finally {
        setPoolCommitmentLoading(false);
      }
    };
    loadDealPoolCommitmentConfig();
  }, [isPlatformStaffRole]);

  const isSellerForClearance = useMemo(() => {
    const r = normalizeRole(currentRole);
    return r === 'vendor' || r === 'used_vendor';
  }, [currentRole]);

  const clearanceRules = dealsConfig?.clearanceRules || {};
  const clearanceMinDiscount = Number(clearanceRules.minDiscountPct) || 20;
  const clearanceMaxDiscount = Number(clearanceRules.maxDiscountPct) || 70;
  const defaultDealPoolCommitmentPct = Math.max(1, Math.min(100, Number(clearanceRules.defaultCommitmentPct) || 20));

  const canManageClearanceDraft = isSellerForClearance || isPlatformStaffRole;

  const loadComboFailures = async () => {
    if (!isPlatformStaffRole) return;
    setComboFailuresLoading(true);
    try {
      const res = await api.get('/admin/deals/combo/failures', { params: { limit: 50 } });
      setComboFailures(res.data?.data?.failures || []);
    } catch (e) {
      setComboFailures([]);
      toast.error(getApiErrorMessage(e, 'Failed to load combo qualification failures'));
    } finally {
      setComboFailuresLoading(false);
    }
  };

  useEffect(() => {
    if (activeCategory === 'combo' && isPlatformStaffRole) {
      loadComboFailures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, isPlatformStaffRole]);

  const loadClearanceDrafts = async () => {
    setClearanceDraftsLoading(true);
    try {
      const endpoint = isPlatformStaffRole
        ? '/admin/deals/clearance/drafts'
        : '/vendor/deals/clearance/drafts';
      const res = await api.get(endpoint);
      const list = res.data?.data?.drafts || [];
      setClearanceDrafts(Array.isArray(list) ? list : []);
      const minD = Number(clearanceRules.minDiscountPct) || 20;
      setClearanceAcceptDiscount((prev) => {
        const next = { ...prev };
        list.forEach((draft) => {
          const id = draft?._id || draft?.id;
          if (id && next[id] == null) next[id] = minD;
        });
        return next;
      });
    } catch (e) {
      setClearanceDrafts([]);
      toast.error(getApiErrorMessage(e, 'Failed to load clearance suggestions'));
    } finally {
      setClearanceDraftsLoading(false);
    }
  };

  useEffect(() => {
    if (activeCategory !== 'clearance') return;
    if (!isPlatformStaffRole && !isSellerForClearance) return;
    loadClearanceDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, isPlatformStaffRole, isSellerForClearance]);

  useEffect(() => {
    setPoolCommitmentDraft(String(defaultDealPoolCommitmentPct));
  }, [defaultDealPoolCommitmentPct]);

  const saveDealPoolCommitment = async () => {
    if (!isPlatformStaffRole) return;
    const nextPct = Math.max(1, Math.min(100, Number(poolCommitmentDraft) || 0));
    if (!Number.isFinite(nextPct) || nextPct <= 0) {
      toast.error('Enter deal pool commitment between 1 and 100.');
      return;
    }
    setPoolCommitmentSaving(true);
    try {
      const res = await api.put('/admin/deals/clearance/config', { defaultCommitmentPct: nextPct });
      const config = res.data?.data?.config || {};
      const savedPct = Math.max(1, Math.min(100, Number(config.defaultCommitmentPct) || nextPct));
      setDealsConfig((prev) => ({
        ...prev,
        clearanceRules: {
          ...(prev.clearanceRules || {}),
          ...config,
          defaultCommitmentPct: savedPct
        }
      }));
      setPoolCommitmentDraft(String(savedPct));
      toast.success(res.data?.message || 'Deal pool commitment updated.');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to update deal pool commitment'));
    } finally {
      setPoolCommitmentSaving(false);
    }
  };

  const runClearanceScan = async () => {
    setClearanceScanLoading(true);
    try {
      const res = await api.post('/admin/deals/clearance/run-scan');
      const summary = res.data?.data?.summary;
      toast.success(
        `Scan done: ${summary?.draftsCreated ?? 0} new draft(s), ${summary?.eligible ?? 0} eligible`
      );
      await loadClearanceDrafts();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Clearance scan failed'));
    } finally {
      setClearanceScanLoading(false);
    }
  };

  const acceptClearanceDraft = async (draftId) => {
    const discountPct = Number(clearanceAcceptDiscount[draftId]);
    if (!Number.isFinite(discountPct)) {
      toast.error('Enter a valid discount %');
      return;
    }
    setClearanceActionLoading((prev) => ({ ...prev, [draftId]: 'accept' }));
    try {
      const acceptPath = isPlatformStaffRole
        ? `/admin/deals/clearance/drafts/${draftId}/accept`
        : `/vendor/deals/clearance/drafts/${draftId}/accept`;
      const res = await api.post(acceptPath, {
        discountPct,
        commitmentPercentage: defaultDealPoolCommitmentPct
      });
      toast.success(res.data?.message || 'Clearance deal created');
      await loadClearanceDrafts();
      const dealsEndpoint = isPlatformStaffRole ? '/admin/deals' : '/deals';
      const dealsRes = await api.get(dealsEndpoint);
      const dealList = dealsRes.data?.data?.deals || [];
      if (Array.isArray(dealList)) setDeals(dealList.map(normalizeDealForUi));
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to accept clearance suggestion'));
    } finally {
      setClearanceActionLoading((prev) => ({ ...prev, [draftId]: null }));
    }
  };

  const rejectClearanceDraft = async (draftId) => {
    setClearanceActionLoading((prev) => ({ ...prev, [draftId]: 'reject' }));
    try {
      const rejectPath = isPlatformStaffRole
        ? `/admin/deals/clearance/drafts/${draftId}/reject`
        : `/vendor/deals/clearance/drafts/${draftId}/reject`;
      const res = await api.post(rejectPath);
      toast.info(res.data?.message || 'Suggestion rejected');
      await loadClearanceDrafts();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to reject clearance suggestion'));
    } finally {
      setClearanceActionLoading((prev) => ({ ...prev, [draftId]: null }));
    }
  };

  const loadCatalogCategories = async () => {
    setCatalogCategoriesLoading(true);
    try {
      const result = await categoryService.getAllActiveCategoriesFlat({ forVendor: true });
      const rows = parseFlatCategoriesResponse(result);
      const names = [...new Set(
        rows
          .map((c) => String(c?.name || c || '').trim())
          .filter(Boolean)
      )].sort((a, b) => a.localeCompare(b));
      setCatalogCategoryOptions(names);
    } catch (e) {
      setCatalogCategoryOptions([]);
      toast.error(getApiErrorMessage(e, 'Failed to load categories'));
    } finally {
      setCatalogCategoriesLoading(false);
    }
  };

  useEffect(() => {
    if (!showCreateModal) return;
    loadCatalogCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal]);

  const loadSellerOptions = async () => {
    if (!isPlatformStaffRole) return;
    setSellerOptionsLoading(true);
    try {
      const users = await userService.getAllUsers();
      const list = (Array.isArray(users) ? users : [])
        .filter((u) => {
          const r = normalizeRole(u?.role);
          return r === 'vendor' || r === 'used_vendor' || r === 'garage';
        })
        .map((u) => {
          const id = String(u._id || u.id || '');
          const name =
            u.vendorDetails?.storeName ||
            u.vendorDetails?.businessName ||
            u.garageDetails?.garageName ||
            u.garageDetails?.name ||
            u.name ||
            'Unnamed seller';
          return {
            id,
            name,
            email: u.email || '',
            role: normalizeRole(u.role)
          };
        })
        .filter((u) => u.id)
        .sort((a, b) => a.name.localeCompare(b.name));
      setSellerOptions(list);
    } catch (_) {
      setSellerOptions([]);
    } finally {
      setSellerOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (!showCreateModal || !isPlatformStaffRole) return;
    loadSellerOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, isPlatformStaffRole]);

  const loadProductOptions = async (dealCategory = form.category) => {
    setProductOptionsLoading(true);
    setProductError('');
    setSelectedProduct(null);
    try {
      let list = [];
      const category = String(dealCategory || '').toLowerCase();
      if (category === 'clearance') {
        const clearanceEndpoint = isPlatformStaffRole
          ? '/admin/deals/clearance/eligible-products'
          : '/vendor/deals/clearance/eligible-products';
        const res = await api.get(clearanceEndpoint, {
          params: isPlatformStaffRole ? { limit: 500 } : {}
        });
        list = res.data?.data?.products || [];
        const rules = res.data?.data?.rules || {};
        if (!list.length) {
          setProductError(
            `No clearance-eligible parts found. Products must have no sale in the last ${rules.triggerDays ?? 45} days and sell rate ≤ ${rules.sellRateThresholdPct ?? 10}% in the last ${rules.windowDays ?? 90} days.`
          );
        }
      } else if (category === 'coupon') {
        setProductOptions([]);
        setProductError('');
        setProductOptionsLoading(false);
        return;
      } else if (category === 'combo') {
        const response = await productService.getAdminProducts({ limit: 500, status: 'approved', isActive: true });
        const raw = Array.isArray(response) ? response : (response.products || response.data?.products || []);
        list = raw.filter((p) => {
          const stock = Number(p?.stock);
          return Number.isFinite(stock) && stock > 0;
        });
      } else if (category === 'seller_own') {
        let raw = [];
        if (isPlatformStaffRole) {
          const sellerId = String(form.sellerOwnSellerId || '').trim();
          if (!sellerId) {
            setProductOptions([]);
            setProductError('');
            setProductOptionsLoading(false);
            return;
          }
          const response = await productService.getAdminProducts({
            limit: 500,
            status: 'approved',
            isActive: true,
            vendorId: sellerId
          });
          raw = Array.isArray(response) ? response : (response.products || response.data?.products || []);
        } else {
          const role = normalizeRole(currentRole);
          if (role === 'garage') {
            const res = await api.get('/garage/products', { params: { limit: 500, status: 'approved' } });
            raw = res.data?.data?.products || res.data?.products || [];
          } else {
            const response = await productService.getVendorProducts({
              limit: 500,
              status: 'approved'
            });
            raw = Array.isArray(response) ? response : (response.products || response.data?.products || []);
          }
        }
        list = raw.filter((p) => {
          const stock = Number(p?.stock);
          const mrp = Number(p?.mrp);
          const price = Number(p?.price);
          const ceiling = Number.isFinite(mrp) && mrp > 0 ? mrp : price;
          return (
            p?.isActive !== false
            && String(p?.status || 'approved').toLowerCase() !== 'rejected'
            && Number.isFinite(stock)
            && stock > 0
            && Number.isFinite(ceiling)
            && ceiling > 0
          );
        });
        if (!list.length) {
          setProductError(
            raw.length
              ? 'No in-stock approved parts with MRP or price. Update the product under Vendor → Products.'
              : 'No eligible spare parts found. Use an approved listing with stock and MRP set (Vendor → Products).'
          );
        }
      } else if (category === 'garage_service') {
        setProductOptions([]);
        setProductError('');
        setProductOptionsLoading(false);
        return;
      } else if (isPlatformStaffRole) {
        const response = await productService.getAdminProducts({ limit: 100 });
        list = Array.isArray(response) ? response : (response.products || response.data?.products || []);
      } else {
        const response = await productService.getUserProducts({ limit: 100 });
        list = Array.isArray(response) ? response : (response.products || response.data?.products || []);
      }
      setProductOptions(Array.isArray(list) ? list : []);
    } catch (e) {
      setProductOptions([]);
      setProductError(getApiErrorMessage(e, 'Failed to load spare parts'));
    } finally {
      setProductOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (!showCreateModal) return;
    setProductError('');
    clearCreateFieldValidation();
    setDealActionFeedback({ type: '', message: '' });
    setCreateDealLoading(false);
    if (form.category !== 'garage_service') {
      loadProductOptions(form.category);
    } else {
      setProductOptions([]);
      setSelectedProduct(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, isSuperAdminRole, form.category]);

  useEffect(() => {
    if (!showCreateModal || form.category !== 'seller_own' || !isPlatformStaffRole) return;
    if (!form.sellerOwnSellerId) {
      setProductOptions([]);
      setSelectedProduct(null);
      return;
    }
    loadProductOptions('seller_own');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.sellerOwnSellerId]);

  const buildCampaignDatesForPayload = () => ({
    startDate: form.startDate ? combineDateTimeLocal(form.startDate, '00:00') : null,
    endDate: form.endDate ? combineDateTimeLocal(form.endDate, '23:59') : null
  });

  const buildScheduledGoLiveForPayload = () => {
    if (form.status !== 'Scheduled') return null;
    return combineDateTimeLocal(form.scheduledGoLiveDate, form.scheduledGoLiveTime || '09:00');
  };

  const isCreateFieldInvalid = (field) => createFieldErrors.includes(field);

  const clearCreateFieldValidation = () => {
    setCreateFieldErrors([]);
    setCreateFieldErrorMessages({});
  };

  const applyCreateFieldValidation = ({ errors, messages }) => {
    setCreateFieldErrors(errors);
    setCreateFieldErrorMessages(messages || {});
  };

  const getCreateFieldProps = (field) => ({
    'data-create-field': field,
    ...(isCreateFieldInvalid(field)
      ? { 'aria-invalid': true, 'aria-describedby': `create-field-error-${field}` }
      : {})
  });

  const renderCreateFieldError = (field) => {
    if (!isCreateFieldInvalid(field)) return null;
    const text = createFieldErrorMessages[field];
    if (!text) return null;
    return (
      <p
        id={`create-field-error-${field}`}
        className="mt-1 text-[11px] font-medium text-red-600"
        role="alert"
      >
        {text}
      </p>
    );
  };

  const withCreateFieldError = (baseClass, field) => {
    if (!isCreateFieldInvalid(field)) return baseClass;
    const withoutBlueFocus = String(baseClass)
      .replace(/\bfocus:ring-primary-\d+\b/g, '')
      .replace(/\bfocus:ring-primary-\d+\/\d+\b/g, '')
      .replace(/\bfocus-visible:ring-primary-\d+\b/g, '')
      .replace(/\bfocus:ring-\d+\b/g, '')
      .replace(/\bfocus-visible:ring-\d+\b/g, '')
      .replace(/\bfocus:border-primary-\d+\b/g, '')
      .replace(/\bfocus-visible:border-primary-\d+\b/g, '')
      .replace(/\bring-\d+\b/g, '')
      .replace(/\bshadow-\S+\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    return `${withoutBlueFocus} deal-field-invalid`;
  };

  const collectCreateDealFieldErrors = () => {
    const errors = [];
    const messages = {};
    const add = (field, msg) => {
      if (!field) return;
      if (!errors.includes(field)) errors.push(field);
      if (msg && !messages[field]) messages[field] = msg;
    };

    const requireCampaignWindow = () => {
      if (!String(form.startDate || '').trim()) {
        add('startDate', 'Campaign start date is required.');
      }
      if (!String(form.endDate || '').trim()) {
        add('endDate', 'Campaign end date is required.');
      }
      if (form.startDate && form.endDate && form.endDate < form.startDate) {
        add('endDate', 'Campaign end must be on or after campaign start.');
      }
    };

    const requireStatusForCategory = () => {
      const flashBrandStatuses = ['Draft', 'Scheduled', 'Live'];
      const standardStatuses = ['Draft', 'Active', 'Scheduled'];
      const allowed = ['flash', 'brand'].includes(form.category) ? flashBrandStatuses : standardStatuses;
      if (!form.status || !allowed.includes(form.status)) {
        add('status', 'Status is required.');
      }
    };

    const requireDiscountPct = (min, max) => {
      const raw = String(form.discountPct ?? '').trim();
      const discount = Number(form.discountPct);
      if (!raw || !Number.isFinite(discount)) {
        add('discountPct', 'Discount (%) is required.');
        return;
      }
      if (discount < min || discount > max) {
        add('discountPct', `Enter discount between ${min}% and ${max}%.`);
      }
    };

    if (!String(form.category || '').trim()) {
      add('category', 'Deal type is required.');
    }

    if (form.status === 'Scheduled') {
      if (!form.scheduledGoLiveDate) {
        add('scheduledGoLiveDate', 'Go-live date is required for scheduled deals.');
      } else {
        const goLive = buildScheduledGoLiveForPayload();
        if (!goLive || new Date(goLive).getTime() <= Date.now()) {
          add('scheduledGoLiveTime', 'Go-live date and time must be in the future.');
        }
      }
    }

    if (form.category === 'flash') {
      if (!String(form.title || '').trim()) {
        add('title', 'Campaign name is required.');
      }
      const selectionType = form.flashSelectionType || 'category';
      if (!['category', 'brand', 'sku'].includes(selectionType)) {
        add('flashSelectionType', "What's on sale is required.");
      }
      const st = selectionType;
      if (st === 'category' && !String(form.flashFilterCategory || '').trim()) {
        add('flashFilterCategory', 'Category is required.');
      }
      if (st === 'brand' && !String(form.flashFilterBrand || '').trim()) {
        add('flashFilterBrand', 'Brand is required.');
      }
      if (st === 'brand' && !String(form.flashFilterProductId || '').trim()) {
        add('flashFilterProductId', 'Spare part is required.');
      }
      if (st === 'sku' && !String(form.flashFilterSku || '').trim()) {
        add('flashFilterSku', 'SKU is required.');
      }
      requireDiscountPct(5, 70);
      requireStatusForCategory();
      requireCampaignWindow();
    } else if (form.category === 'combo') {
      if (!String(form.comboBundleName || '').trim()) {
        add('comboBundleName', 'Bundle name is required.');
      }
      const items = Array.isArray(form.comboItems) ? form.comboItems : [];
      if (items.length < 2) {
        add('comboItem-0', 'Add at least 2 products to the combo bundle.');
        add('comboItem-1', 'Add at least 2 products to the combo bundle.');
      }
      items.forEach((item, idx) => {
        if (!String(item?.productId || '').trim()) {
          add(`comboItem-${idx}`, 'Select a product for each row in the bundle.');
        }
      });
      if (!Number.isFinite(Number(form.comboDiscountPct)) || String(form.comboDiscountPct).trim() === '') {
        add('comboDiscountPct', 'Combo discount (%) is required.');
      }
      requireStatusForCategory();
      requireCampaignWindow();
    } else if (form.category === 'coupon') {
      const code = String(form.couponCode || '').trim();
      if (!code) {
        add('couponCode', 'Coupon code is required.');
      }
      const scopeType = form.couponScopeType === 'category' ? 'category' : 'all';
      const scopeLabelValue = String(form.couponScopeLabel || '').trim();
      if (!['all', 'category'].includes(form.couponScopeType)) {
        add('couponScopeType', 'Applicable scope is required.');
      }
      if (scopeType === 'category' && !scopeLabelValue) {
        add('couponScopeLabel', 'Product category is required.');
      }
      const discountType = form.couponDiscountType === 'amount' ? 'amount' : 'percent';
      const fixedAmount = Math.max(0, Number(form.couponDiscountAmount) || 0);
      const discount = Math.max(0, Math.min(100, Number(form.discountPct) || 0));
      if (discountType === 'amount' && fixedAmount <= 0) {
        add('couponDiscountAmount', 'Discount amount is required.');
      }
      if (discountType === 'percent' && discount <= 0) {
        add('discountPct', 'Discount (%) is required.');
      }
      if (
        form.couponMinOrderValue === '' ||
        form.couponMinOrderValue === null ||
        !Number.isFinite(Number(form.couponMinOrderValue))
      ) {
        add('couponMinOrderValue', 'Min order value is required.');
      }
      const usageLimit = Number(form.couponUsageLimitPerUser);
      if (!Number.isFinite(usageLimit) || usageLimit < 1) {
        add('couponUsageLimitPerUser', 'Per-user limit must be at least 1.');
      }
      requireStatusForCategory();
      requireCampaignWindow();
    } else if (form.category === 'garage_service') {
      if (!String(form.title || '').trim()) {
        add('title', 'Deal title is required.');
      }
      const serviceId = String(form.garageServiceId || '').trim();
      if (!serviceId) {
        add('garageServiceId', 'Service is required.');
      }
      const campaignCities = (Array.isArray(form.garageServiceCities) ? form.garageServiceCities : [])
        .map((c) => String(c || '').trim())
        .filter(Boolean);
      if (isAdminGarageCampaignForm && !campaignCities.length) {
        add('garageServiceCities', 'Select at least one city.');
      }
      const discountType = form.garageDiscountType === 'amount' ? 'amount' : 'percent';
      const fixedAmount = Math.max(0, Number(form.garageDiscountAmount) || 0);
      const discount = Math.max(0, Math.min(100, Number(form.discountPct) || 0));
      if (discountType === 'amount' && fixedAmount <= 0) {
        add('garageDiscountAmount', 'Discount amount is required.');
      }
      if (discountType === 'percent' && discount <= 0) {
        add('discountPct', 'Discount (%) is required.');
      }
      requireStatusForCategory();
      requireCampaignWindow();
    } else if (form.category === 'brand') {
      if (!String(form.brandCampaignBrand || '').trim()) {
        add('brandCampaignBrand', 'Brand is required.');
      }
      if (!String(form.brandCategoryFilter || '').trim()) {
        add('brandCategoryFilter', 'Category is required.');
      }
      const budget = Math.max(0, Number(form.brandBudgetTotal) || 0);
      if (budget <= 0) {
        add('brandBudgetTotal', 'Campaign budget is required.');
      }
      if (brandPreview && brandPreview.canCreate === false) {
        add('brandCampaignBrand', brandPreview.message || 'No eligible products for this brand.');
      }
      requireDiscountPct(5, 70);
      requireStatusForCategory();
      requireCampaignWindow();
    } else if (form.category === 'clearance') {
      if (!String(form.title || '').trim()) {
        add('title', 'Deal title is required.');
      }
      if (!Number.isFinite(Number(form.discountPct))) {
        add('discountPct', `Enter discount between ${clearanceMinDiscount}% and ${clearanceMaxDiscount}%.`);
      } else {
        const discount = Number(form.discountPct);
        if (discount < clearanceMinDiscount || discount > clearanceMaxDiscount) {
          add('discountPct', `Enter discount between ${clearanceMinDiscount}% and ${clearanceMaxDiscount}%.`);
        }
      }
      if (!pendingClearanceDraftId) {
        if (!selectedProduct || !getProductId(selectedProduct)) {
          add('product', 'Select a slow-moving spare part for this clearance deal.');
        } else {
          const pid = getProductId(selectedProduct);
          const liveStock = stockByProductId[pid] ?? getProductStock(selectedProduct);
          if (liveStock === 0 || liveStock < 0) {
            add('product', 'Selected spare part is out of stock.');
          }
        }
      }
      requireStatusForCategory();
      requireCampaignWindow();
    } else {
      if (form.category === 'seller_own' && isPlatformStaffRole && !String(form.sellerOwnSellerId || '').trim()) {
        add('sellerOwnSellerId', 'Seller ID is required.');
      }
      if (!String(form.title || '').trim()) {
        add('title', 'Deal title is required.');
      }
      if (!selectedProduct || !getProductId(selectedProduct)) {
        add('product', 'Spare part is required.');
      } else {
        const pid = String(getProductId(selectedProduct) || '').trim();
        if (!pid) {
          add('product', 'Spare part is required.');
        } else {
          const liveStock = stockByProductId[pid] ?? getProductStock(selectedProduct);
          if (liveStock === 0 || liveStock < 0) {
            add('product', 'Selected spare part is out of stock.');
          } else {
            const mrp = Number(selectedProduct?.mrp);
            const price = Number(selectedProduct?.price);
            const ceiling = Number.isFinite(mrp) && mrp > 0 ? mrp : price;
            if (!Number.isFinite(ceiling) || ceiling <= 0) {
              add('product', 'Set MRP or price on the product before creating a deal.');
            } else if (
              dealCategoryRequiresStructuredAttributes(form.category) &&
              !isProductDealReady(selectedProduct)
            ) {
              add('product', formatDealEligibilityError(selectedProduct, form.category));
            }
          }
        }
      }
      requireDiscountPct(1, 100);
      requireStatusForCategory();
      requireCampaignWindow();
    }

    if (
      form.category === 'seller_own'
      && isPlatformStaffRole
      && sellerOwnOtpStep === 'sent'
      && !/^\d{6}$/.test(String(sellerOwnOtp || '').trim())
    ) {
      add('sellerOwnOtp', 'Enter the 6-digit OTP sent to the seller.');
    }

    const firstMessage = errors.map((f) => messages[f]).find(Boolean) || '';
    return { errors, messages, message: firstMessage };
  };

  useEffect(() => {
    if (!showCreateModal || createFieldErrors.length === 0) return;
    const { errors: nextErrors, messages: nextMessages } = collectCreateDealFieldErrors();
    setCreateFieldErrors(nextErrors);
    setCreateFieldErrorMessages(nextMessages);
    if (nextErrors.length === 0) setProductError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, selectedProduct, showCreateModal, stockByProductId, brandPreview, pendingClearanceDraftId, sellerOwnOtp, sellerOwnOtpStep]);

  useEffect(() => {
    if (!showCreateModal || !shouldFocusCreateFieldErrorRef.current || createFieldErrors.length === 0) return;
    shouldFocusCreateFieldErrorRef.current = false;
    const firstField = createFieldErrors[0];
    const escapedField =
      firstField && typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(firstField)
        : String(firstField || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const selector = firstField
      ? `.deal-create-form [data-create-field="${escapedField}"]`
      : '.deal-create-form .deal-field-invalid';
    const firstInvalid = document.querySelector(selector);
    if (!firstInvalid) return;
    if (typeof firstInvalid.focus === 'function') {
      firstInvalid.focus({ preventScroll: false });
    }
    firstInvalid.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [createFieldErrors, showCreateModal]);

  const buildDealTimingPayload = () => ({
    ...buildCampaignDatesForPayload(),
    scheduledGoLiveAt: buildScheduledGoLiveForPayload()
  });

  const focusFirstCreateFieldError = () => {
    shouldFocusCreateFieldErrorRef.current = true;
  };

  const validateScheduledStatusBeforeSave = () => {
    if (form.status !== 'Scheduled') return true;
    const { errors, messages } = collectCreateDealFieldErrors();
    const scheduledErrors = errors.filter((f) =>
      ['scheduledGoLiveDate', 'scheduledGoLiveTime'].includes(f)
    );
    if (!scheduledErrors.length) return true;
    applyCreateFieldValidation({
      errors: Array.from(new Set([...createFieldErrors, ...scheduledErrors])),
      messages: { ...createFieldErrorMessages, ...messages }
    });
    setProductError('');
    focusFirstCreateFieldError();
    return false;
  };

  const renderScheduledGoLiveFields = (variant = 'white') => {
    if (form.status !== 'Scheduled') return null;
    const inputClass = variant === 'slate'
      ? 'mt-2 w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-200'
      : 'mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200';
    return (
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4 space-y-3">
        <p className="text-xs font-bold text-indigo-900 uppercase tracking-wider">Schedule go-live</p>
        <p className="text-[11px] text-indigo-800">
          When the deal should automatically go live and appear on the website.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Go live — date<RequiredMark /></label>
            <input
              type="date"
              value={form.scheduledGoLiveDate}
              onChange={(e) => setForm((p) => ({ ...p, scheduledGoLiveDate: e.target.value }))}
              className={withCreateFieldError(inputClass, 'scheduledGoLiveDate')}
              {...getCreateFieldProps('scheduledGoLiveDate')}
            />
            {renderCreateFieldError('scheduledGoLiveDate')}
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Go live — time<RequiredMark /></label>
            <input
              type="time"
              value={form.scheduledGoLiveTime}
              onChange={(e) => setForm((p) => ({ ...p, scheduledGoLiveTime: e.target.value }))}
              className={withCreateFieldError(inputClass, 'scheduledGoLiveTime')}
              {...getCreateFieldProps('scheduledGoLiveTime')}
            />
            {renderCreateFieldError('scheduledGoLiveTime')}
          </div>
        </div>
      </div>
    );
  };

  const renderCampaignWindowFields = (variant = 'white') => {
    const inputClass = variant === 'slate'
      ? 'mt-2 w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-200'
      : 'mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200';
    const dateInputClass = variant === 'slate'
      ? 'w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-200'
      : 'w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200';
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Campaign start<RequiredMark /></label>
          <input
            type="date"
            value={form.startDate}
            onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
            className={withCreateFieldError(dateInputClass, 'startDate')}
            {...getCreateFieldProps('startDate')}
          />
          {renderCreateFieldError('startDate')}
          <p className="text-[10px] text-slate-500 mt-1">When buyers can use the deal after it is live.</p>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Campaign end<RequiredMark /></label>
          <input
            type="date"
            value={form.endDate}
            onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
            className={withCreateFieldError(dateInputClass, 'endDate')}
            {...getCreateFieldProps('endDate')}
          />
          {renderCreateFieldError('endDate')}
          <p className="text-[10px] text-slate-500 mt-1">Deal stops for buyers after this date.</p>
        </div>
      </div>
    );
  };

  const renderDealTimingSection = (variant = 'white') => (
    <div className="space-y-4">
      {renderScheduledGoLiveFields(variant)}
      {renderCampaignWindowFields(variant)}
    </div>
  );

  const buildComboConfigPayload = () => ({
    discountPct: Math.max(0, Math.min(100, Number(form.comboDiscountPct) || 0)),
    items: (Array.isArray(form.comboItems) ? form.comboItems : []).map((item) => ({
      productId: String(item?.productId || '').trim()
    }))
  });

  const getComboSelectableProducts = (rowIndex) => {
    const selectedElsewhere = new Set(
      (Array.isArray(form.comboItems) ? form.comboItems : [])
        .map((item, i) => (i !== rowIndex ? String(item?.productId || '').trim() : ''))
        .filter(Boolean)
    );
    return (productOptions || []).filter((p) => {
      const id = String(getProductId(p) || '');
      if (!id || selectedElsewhere.has(id)) return false;
      const stock = getProductStock(p);
      return stock == null || stock > 0;
    });
  };

  const comboPreviewReady = useMemo(() => {
    if (form.category !== 'combo') return false;
    const items = Array.isArray(form.comboItems) ? form.comboItems : [];
    if (items.length < 2) return false;
    const ids = items.map((item) => String(item?.productId || '').trim()).filter(Boolean);
    if (ids.length < 2 || ids.length !== items.length) return false;
    return new Set(ids).size === ids.length;
  }, [form.category, form.comboItems]);

  const isCreateDealSubmitReady = useMemo(() => {
    if (form.category === 'coupon') {
      const code = String(form.couponCode || '').trim();
      if (!code) return false;
      if (form.couponScopeType === 'category' && !String(form.couponScopeLabel || '').trim()) {
        return false;
      }
      if (form.couponDiscountType === 'amount') {
        return Math.max(0, Number(form.couponDiscountAmount) || 0) > 0;
      }
      return Math.max(0, Math.min(100, Number(form.discountPct) || 0)) > 0;
    }
    if (form.category === 'flash') {
      if (!String(form.title || '').trim()) return false;
      const st = form.flashSelectionType || 'category';
      if (st === 'category' && !String(form.flashFilterCategory || '').trim()) return false;
      if (st === 'brand' && !String(form.flashFilterBrand || '').trim()) return false;
      if (st === 'brand' && !String(form.flashFilterProductId || '').trim()) return false;
      if (st === 'sku' && !String(form.flashFilterSku || '').trim()) return false;
      return true;
    }
    if (form.category === 'combo') {
      return comboPreviewReady && !!String(form.comboBundleName || '').trim();
    }
    if (form.category === 'brand') {
      if (!String(form.brandCampaignBrand || '').trim()) return false;
      if (!String(form.brandCategoryFilter || '').trim()) return false;
      if (Math.max(0, Number(form.brandBudgetTotal) || 0) <= 0) return false;
      if (brandPreview && brandPreview.canCreate === false) return false;
      return true;
    }
    if (form.category === 'garage_service') {
      if (!String(form.title || '').trim()) return false;
      if (!String(form.garageServiceId || '').trim()) return false;
      if (isAdminGarageCampaignForm && (!Array.isArray(form.garageServiceCities) || form.garageServiceCities.length === 0)) return false;
      if (form.garageDiscountType === 'amount') {
        return Math.max(0, Number(form.garageDiscountAmount) || 0) > 0;
      }
      return Math.max(0, Math.min(100, Number(form.discountPct) || 0)) > 0;
    }
    if (form.category === 'seller_own' && isPlatformStaffRole && !String(form.sellerOwnSellerId || '').trim()) {
      return false;
    }
    if (
      form.category === 'seller_own'
      && isPlatformStaffRole
      && sellerOwnOtpStep === 'sent'
      && !/^\d{6}$/.test(String(sellerOwnOtp || '').trim())
    ) {
      return false;
    }
    if (form.category === 'clearance') {
      if (!selectedProduct || !getProductId(selectedProduct)) return false;
      const discount = Number(form.discountPct);
      if (!Number.isFinite(discount) || discount < clearanceMinDiscount || discount > clearanceMaxDiscount) {
        return false;
      }
      const pid = getProductId(selectedProduct);
      const stock = pid ? (stockByProductId[pid] ?? getProductStock(selectedProduct)) : null;
      return !(stock === 0 || stock < 0);
    }
    if (!String(form.title || '').trim()) return false;
    if (!selectedProduct || !getProductId(selectedProduct)) return false;
    const pid = getProductId(selectedProduct);
    const stock = pid ? (stockByProductId[pid] ?? getProductStock(selectedProduct)) : null;
    if (stock === 0 || stock < 0) return false;
    const mrp = Number(selectedProduct?.mrp);
    const price = Number(selectedProduct?.price);
    const ceiling = Number.isFinite(mrp) && mrp > 0 ? mrp : price;
    if (!Number.isFinite(ceiling) || ceiling <= 0) return false;
    if (
      dealCategoryRequiresStructuredAttributes(form.category) &&
      !isProductDealReady(selectedProduct)
    ) {
      return false;
    }
    return true;
  }, [
    form,
    selectedProduct,
    stockByProductId,
    comboPreviewReady,
    brandPreview,
    clearanceMinDiscount,
    clearanceMaxDiscount,
    isAdminGarageCampaignForm,
    isPlatformStaffRole,
    sellerOwnOtp,
    sellerOwnOtpStep
  ]);

  const previewComboQualification = async ({ silent = false } = {}) => {
    if (!comboPreviewReady) {
      setComboQualificationReport(null);
      return;
    }
    setComboPreviewLoading(true);
    if (!silent) setProductError('');
    try {
      const res = await api.post('/admin/deals/combo/preview-qualification', {
        comboConfig: buildComboConfigPayload()
      });
      const report = res.data?.data?.qualificationReport;
      setComboQualificationReport(report || null);
      if (!silent) {
        if (report?.qualifyingSellerCount > 0) {
          toast.success(`${report.qualifyingSellerCount} seller(s) can fulfill this bundle.`);
        } else {
          toast.warning(report?.suggestedFix || 'No qualifying sellers found.');
        }
      }
    } catch (e) {
      setComboQualificationReport(null);
      if (!silent) {
        setProductError(getApiErrorMessage(e, 'Failed to load combo price'));
      }
    } finally {
      setComboPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!showCreateModal || form.category !== 'brand') {
      setBrandPreview(null);
      return undefined;
    }
    const brand = String(form.brandCampaignBrand || '').trim();
    if (!brand) {
      setBrandPreview(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setBrandPreviewLoading(true);
      try {
        const res = await api.get('/admin/deals/brand/preview', {
          params: {
            brand,
            categoryFilter: String(form.brandCategoryFilter || '').trim() || undefined
          }
        });
        if (!cancelled) {
          setBrandPreview(res.data?.data || { canCreate: true });
        }
      } catch (e) {
        if (!cancelled) {
          setBrandPreview({
            canCreate: false,
            message: getApiErrorMessage(e, 'Cannot create campaign for this brand yet.'),
            ...(e?.response?.data?.data || {})
          });
        }
      } finally {
        if (!cancelled) setBrandPreviewLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [showCreateModal, form.category, form.brandCampaignBrand, form.brandCategoryFilter]);

  useEffect(() => {
    if (!showCreateModal || form.category !== 'combo') {
      setComboQualificationReport(null);
      return undefined;
    }
    if (!comboPreviewReady) {
      setComboQualificationReport(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      previewComboQualification({ silent: true });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, form.category, comboPreviewReady, form.comboDiscountPct, form.comboItems]);

  const selectProductWithStockCheck = async (p) => {
    const id = getProductId(p);
    if (!id) return;
    setProductLoading(true);
    setProductError('');
    try {
      const product = await fetchProductById(id);
      const stock = getProductStock(product);
      setStockByProductId((prev) => ({ ...prev, [id]: stock }));

      if (form.category !== 'garage_service' && (stock === 0 || stock < 0)) {
        setSelectedProduct(null);
        setProductError('This spare part is out of stock. You cannot create a deal for it.');
        return;
      }

      if (
        dealCategoryRequiresStructuredAttributes(form.category) &&
        !isProductDealReady(product)
      ) {
        setSelectedProduct(null);
        setProductError(formatDealEligibilityError(product, form.category));
        return;
      }

      // Use fetched product so we have latest stock/price fields
      setSelectedProduct(product);
    } catch (e) {
      setSelectedProduct(null);
      setProductError(e?.message || 'Failed to fetch spare part details');
    } finally {
      setProductLoading(false);
    }
  };

  const saveDeal = async (e) => {
    e.preventDefault();
    const { errors: fieldErrors, messages: fieldMessages } = collectCreateDealFieldErrors();
    if (fieldErrors.length) {
      focusFirstCreateFieldError();
      applyCreateFieldValidation({ errors: fieldErrors, messages: fieldMessages });
      setProductError('');
      return;
    }
    clearCreateFieldValidation();
    setProductError('');
    const role = normalizeRole(currentRole);
    const platformStaff = role === 'superadmin' || role === 'admin';
    const allowed = DEAL_CREATOR_POLICY[form.category] || [];
    if (!platformStaff && !allowed.includes(role)) {
      setProductError(`Role ${role} cannot create ${form.category} deals.`);
      return;
    }

    const displayStatus = ['flash', 'brand'].includes(form.category)
      ? (form.status === 'Inactive' ? 'Draft' : form.status === 'Active' ? 'Live' : form.status)
      : form.status;

    if (!validateScheduledStatusBeforeSave()) return;

    let payload = { category: form.category, status: displayStatus };

    if (form.category === 'flash') {
      if (!form.title.trim()) {
        setProductError('Campaign name is required.');
        return;
      }
      const st = form.flashSelectionType || 'category';
      if (st === 'category' && !String(form.flashFilterCategory || '').trim()) {
        setProductError('Select or enter a category for what is on sale.');
        return;
      }
      if (st === 'brand' && !String(form.flashFilterBrand || '').trim()) {
        setProductError('Select or enter a brand for what is on sale.');
        return;
      }
      if (st === 'brand' && !String(form.flashFilterProductId || '').trim()) {
        setProductError('Select a spare part for this flash sale.');
        return;
      }
      if (st === 'sku' && !String(form.flashFilterSku || '').trim()) {
        setProductError('Enter at least one SKU for what is on sale.');
        return;
      }
      const discount = Math.max(5, Math.min(70, Number(form.discountPct) || 0));
      payload = {
        ...payload,
        title: form.title.trim(),
        campaignName: form.title.trim(),
        discountPct: discount,
        ...buildDealTimingPayload(),
        startDate: combineDateTimeLocal(form.startDate, form.startTime),
        endDate: combineDateTimeLocal(form.endDate, form.endTime),
        flashConfig: {
          selectionType: st,
          filterBrand: String(form.flashFilterBrand || '').trim(),
          filterCategory: String(form.flashFilterCategory || '').trim(),
          filterSku: String(form.flashFilterSku || '').trim()
        }
      };
      if (st === 'brand' && String(form.flashFilterProductId || '').trim()) {
        payload.productId = String(form.flashFilterProductId).trim();
      }
    } else if (form.category === 'combo') {
      if (!String(form.comboBundleName || '').trim()) {
        setProductError('Bundle name is required.');
        return;
      }
      const items = Array.isArray(form.comboItems) ? form.comboItems : [];
      if (items.length < 2) {
        setProductError('Add at least 2 products to the combo bundle.');
        return;
      }
      if (items.some((item) => !String(item?.productId || '').trim())) {
        setProductError('Select a product for each row in the bundle.');
        return;
      }
      const ids = items.map((item) => String(item.productId).trim());
      if (new Set(ids).size !== ids.length) {
        setProductError('Each product can only be selected once in the bundle.');
        return;
      }
      const discount = Math.max(0, Math.min(100, Number(form.comboDiscountPct) || 0));
      if (!Number.isFinite(Number(form.comboDiscountPct))) {
        setProductError('Combo discount (%) is required.');
        return;
      }
      payload = {
        ...payload,
        title: form.comboBundleName.trim(),
        campaignName: form.comboBundleName.trim(),
        discountPct: discount,
        ...buildDealTimingPayload(),
        comboConfig: buildComboConfigPayload()
      };
    } else if (form.category === 'coupon') {
      const code = String(form.couponCode || '').trim().toUpperCase();
      if (!code) {
        setProductError('Coupon code is required.');
        return;
      }
      const scopeType = form.couponScopeType === 'category' ? 'category' : 'all';
      const scopeLabelValue = String(form.couponScopeLabel || '').trim();
      if (scopeType === 'category' && !scopeLabelValue) {
        setProductError('Select a product category, or choose “All products”.');
        return;
      }
      const discountType = form.couponDiscountType === 'amount' ? 'amount' : 'percent';
      const fixedAmount = Math.max(0, Number(form.couponDiscountAmount) || 0);
      const discount = Math.max(0, Math.min(100, Number(form.discountPct) || 0));
      if (discountType === 'amount' && fixedAmount <= 0) {
        setProductError('Enter a discount amount greater than zero.');
        return;
      }
      if (discountType === 'percent' && discount <= 0) {
        setProductError('Enter a discount percentage greater than zero.');
        return;
      }
      payload = {
        ...payload,
        title: String(form.title || `Coupon ${code}`).trim(),
        campaignName: String(form.title || `Coupon ${code}`).trim(),
        discountPct: discountType === 'percent' ? discount : 0,
        ...buildDealTimingPayload(),
        couponConfig: {
          code,
          discountType,
          discountAmount: discountType === 'amount' ? fixedAmount : 0,
          minOrderValue: Math.max(0, Number(form.couponMinOrderValue) || 0),
          maxDiscountAmount: discountType === 'percent'
            ? Math.max(0, Number(form.couponMaxDiscountAmount) || 0)
            : 0,
          usageLimitPerUser: Math.max(1, Number(form.couponUsageLimitPerUser) || 1),
          totalUsageLimit: 0,
          firstOrderOnly: !!form.couponFirstOrderOnly,
          stackableWithDeals: !!form.couponStackableWithDeals,
          applicableScope: {
            type: scopeType,
            value: scopeType === 'all' ? '' : scopeLabelValue,
            label: scopeType === 'all' ? 'All products' : scopeLabelValue
          }
        }
      };
    } else if (form.category === 'garage_service') {
      if (!form.title.trim()) {
        setProductError('Deal title is required.');
        return;
      }
      const serviceId = String(form.garageServiceId || '').trim();
      const catalogLines = isAdminGarageCampaignForm
        ? getFullCatalogServiceLines('large')
        : garageServiceOptions;
      const selectedLine = findServiceLineById(catalogLines, serviceId);
      const serviceType = selectedLine?.label || '';
      const campaignCities = (Array.isArray(form.garageServiceCities) ? form.garageServiceCities : [])
        .map((c) => String(c || '').trim())
        .filter(Boolean);
      if (!serviceId || !serviceType) {
        setProductError('Select a service for this deal.');
        return;
      }
      if (
        !isAdminGarageCampaignForm &&
        isPackageDealBlockedForTier(garageProfileTier, serviceId)
      ) {
        setProductError('Periodic packages and AMC deals require a Medium or Large garage.');
        return;
      }
      if (isAdminGarageCampaignForm && !campaignCities.length) {
        setProductError('Select at least one city where garages can join this campaign.');
        return;
      }
      const discountType = form.garageDiscountType === 'amount' ? 'amount' : 'percent';
      const fixedAmount = Math.max(0, Number(form.garageDiscountAmount) || 0);
      const discount = Math.max(0, Math.min(100, Number(form.discountPct) || 0));
      if (discountType === 'amount' && fixedAmount <= 0) {
        setProductError('Enter a discount amount greater than zero.');
        return;
      }
      if (discountType === 'percent' && discount <= 0) {
        setProductError('Enter a discount percentage greater than zero.');
        return;
      }
      const garagePct = discountType === 'percent'
        ? discount
        : GARAGE_APPROVAL_LOCKED_BASE > 0
          ? Number(((fixedAmount / GARAGE_APPROVAL_LOCKED_BASE) * 100).toFixed(2))
          : 0;
      if (garageDiscountNeedsAdminApproval(garagePct) && displayStatus === 'Active') {
        toast.warning(GARAGE_ACTIVE_REQUIRES_ADMIN_MSG);
      }
      if (isAdminGarageCampaignForm) {
        const draftPin = normalizeGaragePincodeInput(form.garagePincodeDraft);
        if (draftPin && draftPin.length !== GARAGE_PINCODE_LENGTH) {
          setGaragePincodeError('Pincode must be exactly 6 digits. Add it or clear the field.');
          return;
        }
      }
      const serviceAreaPincodes = isAdminGarageCampaignForm
        ? (Array.isArray(form.garageServiceAreaPincodes) ? form.garageServiceAreaPincodes : [])
        : [];
      payload = {
        ...payload,
        title: form.title.trim(),
        campaignName: form.title.trim(),
        discountPct: discountType === 'percent' ? discount : 0,
        status: isAdminGarageCampaignForm
          ? displayStatus
          : garageDiscountNeedsAdminApproval(garagePct)
            ? 'Draft'
            : displayStatus,
        ...buildDealTimingPayload(),
        garageServiceConfig: {
          serviceId,
          serviceType,
          ...(isAdminGarageCampaignForm
            ? { cities: campaignCities, city: campaignCities[0] || '', serviceAreaPincodes }
            : {}),
          discountType,
          discountAmount: discountType === 'amount' ? fixedAmount : 0,
          requiresVehicleNumber: !!form.garageRequiresVehicleNumber
        }
      };
    } else if (form.category === 'brand') {
      if (!String(form.brandCampaignBrand || '').trim()) {
        setProductError('Select a brand for the campaign.');
        return;
      }
      if (!String(form.brandCategoryFilter || '').trim()) {
        setProductError('Select a category for the campaign.');
        return;
      }
      const budget = Math.max(0, Number(form.brandBudgetTotal) || 0);
      if (budget <= 0) {
        setProductError('Campaign budget must be greater than zero.');
        return;
      }
      const discount = Math.max(5, Math.min(70, Number(form.discountPct) || 0));
      const brandName = String(form.brandCampaignBrand).trim();
      payload = {
        ...payload,
        title: `${brandName} Campaign`,
        campaignName: `${brandName} Campaign`,
        discountPct: discount,
        ...buildDealTimingPayload(),
        brandConfig: {
          campaignBrand: brandName,
          categoryFilter: String(form.brandCategoryFilter || '').trim(),
          budgetTotal: budget,
          selectionType: 'brand',
          filterBrand: brandName,
          filterCategory: String(form.brandCategoryFilter || '').trim()
        }
      };
      if (String(form.brandProductId || '').trim()) {
        payload.productId = String(form.brandProductId).trim();
      }
    } else if (form.category === 'clearance') {
      const discount = Math.max(
        clearanceMinDiscount,
        Math.min(clearanceMaxDiscount, Number(form.discountPct) || clearanceMinDiscount)
      );
      if (!Number.isFinite(Number(form.discountPct))) {
        setProductError(`Enter discount between ${clearanceMinDiscount}% and ${clearanceMaxDiscount}%.`);
        return;
      }
      const commitmentPct = Math.max(
        1,
        Math.min(100, Number(form.commitmentPercentage) || defaultDealPoolCommitmentPct)
      );
      if (!pendingClearanceDraftId) {
        if (!selectedProduct || !getProductId(selectedProduct)) {
          setProductError('Select a slow-moving spare part for this clearance deal.');
          return;
        }
        const pid = getProductId(selectedProduct);
        const liveStock = stockByProductId[pid] ?? getProductStock(selectedProduct);
        if (liveStock === 0 || liveStock < 0) {
          setProductError('Selected spare part is out of stock. Deal cannot be created.');
          return;
        }
        const autoTitle = `Clearance — ${getProductName(selectedProduct)}`.slice(0, 120);
        payload = {
          ...payload,
          title: autoTitle,
          campaignName: autoTitle,
          discountPct: discount,
          productId: pid,
          commitmentPercentage: commitmentPct,
          ...buildDealTimingPayload()
        };
      } else {
        const draftTitle =
          form.title.trim() || `Clearance — ${getProductName(selectedProduct)}`.slice(0, 120);
        payload = {
          ...payload,
          title: draftTitle,
          campaignName: draftTitle,
          discountPct: discount,
          commitmentPercentage: commitmentPct,
          ...buildDealTimingPayload()
        };
      }
    } else {
      if (form.category === 'seller_own' && isPlatformStaffRole && !String(form.sellerOwnSellerId || '').trim()) {
        setProductError('Select a seller ID.');
        return;
      }
      if (!form.title.trim()) return;
      if (!selectedProduct || !getProductId(selectedProduct)) {
        setProductError('Select a spare part for this deal.');
        return;
      }
      const pid = String(getProductId(selectedProduct) || '').trim();
      if (!pid) {
        setProductError('Select a spare part for this deal.');
        return;
      }
      const liveStock = stockByProductId[pid] ?? getProductStock(selectedProduct);
      if (liveStock === 0 || liveStock < 0) {
        setProductError('Selected spare part stock is over. Deal cannot be created.');
        return;
      }
      const mrp = Number(selectedProduct?.mrp);
      const price = Number(selectedProduct?.price);
      const ceiling = Number.isFinite(mrp) && mrp > 0 ? mrp : price;
      if (!Number.isFinite(ceiling) || ceiling <= 0) {
        setProductError('This spare part has no MRP or price. Set pricing on the product (Vendor → Products) before creating a deal.');
        return;
      }
      if (
        dealCategoryRequiresStructuredAttributes(form.category) &&
        !isProductDealReady(selectedProduct)
      ) {
        setProductError(formatDealEligibilityError(selectedProduct, form.category));
        return;
      }
      const discount = Math.max(0, Math.min(100, Number(form.discountPct) || 0));
      payload = {
        ...payload,
        title: form.title.trim(),
        campaignName: form.title.trim(),
        discountPct: discount,
        ...buildDealTimingPayload(),
        productId: pid
      };
      if (showPoolCommitment) {
        payload.commitmentPercentage = Math.max(
          1,
          Math.min(100, Number(form.commitmentPercentage) || defaultDealPoolCommitmentPct)
        );
      }
      if (form.category === 'seller_own' && isPlatformStaffRole) {
        payload.sellerId = String(form.sellerOwnSellerId || '').trim();
      }
    }

    payload.eligibleRoles = canManageParticipationRoles
      ? (Array.isArray(form.eligibleRoles) ? form.eligibleRoles : [])
      : [];

    if (
      form.requestHomepagePlacement &&
      form.category === 'seller_own' &&
      !isPlatformStaffRole
    ) {
      payload.homepagePlacement = { requested: true };
    }

    if (pendingClearanceDraftId && form.category === 'clearance') {
      setCreateDealLoading(true);
      setProductError('');
      setDealActionFeedback({ type: '', message: '' });
      try {
        const timing = buildDealTimingPayload();
        const acceptPath = isPlatformStaffRole
          ? `/admin/deals/clearance/drafts/${pendingClearanceDraftId}/accept`
          : `/vendor/deals/clearance/drafts/${pendingClearanceDraftId}/accept`;
        const res = await api.post(acceptPath, {
          discountPct: payload.discountPct,
          endDate: timing.endDate,
          commitmentPercentage: payload.commitmentPercentage
        });
        const created = res.data?.data?.deal;
        if (created) {
          setDeals((prev) => [normalizeDealForUi(created), ...prev.map(normalizeDealForUi)]);
        }
        toast.success(res.data?.message || 'Clearance deal created');
        await loadClearanceDrafts();
        setPendingClearanceDraftId(null);
        closeCreateModal();
      } catch (err) {
        const message = getApiErrorMessage(err, 'Failed to accept clearance suggestion');
        toast.error(message);
        setProductError(message);
      } finally {
        setCreateDealLoading(false);
      }
      return;
    }

    const needsSellerOtp = platformStaff && form.category === 'seller_own';

    if (needsSellerOtp && sellerOwnOtpStep !== 'sent') {
      const sellerId = String(payload.sellerId || form.sellerOwnSellerId || '').trim();
      if (!sellerId) {
        setProductError('Select a seller ID before sending OTP.');
        return;
      }
      await sendSellerOwnDealOtp(sellerId);
      return;
    }

    if (needsSellerOtp) {
      const otp = String(sellerOwnOtp || '').trim();
      if (!/^\d{6}$/.test(otp)) {
        applyCreateFieldValidation({
          errors: ['sellerOwnOtp'],
          messages: { sellerOwnOtp: 'Enter the 6-digit OTP sent to the seller.' }
        });
        focusFirstCreateFieldError();
        setProductError('');
        return;
      }
    }

    setCreateDealLoading(true);
    setProductError('');
    setDealActionFeedback({ type: '', message: '' });
    try {
      const res = await api.post(
        needsSellerOtp ? '/admin/deals/seller-own/confirm' : '/admin/deals',
        needsSellerOtp ? { ...payload, otp: String(sellerOwnOtp || '').trim() } : payload
      );
      const created = res.data?.data?.deal;
      if (created) {
        const normalizedCreated = normalizeDealForUi(created);
        const isPendingApproval = ['pending_admin', 'pending_superadmin'].includes(
          String(normalizedCreated.approvalStatus || '')
        );
        if (!isPendingApproval || isPlatformStaffRole) {
        setDeals((prev) => [normalizedCreated, ...prev.map(normalizeDealForUi)]);
        if (activeCategory !== 'all' && activeCategory !== normalizedCreated.category) {
          setActiveCategory(normalizedCreated.category || 'all');
          }
        }
        if (isPendingApproval && isPlatformStaffRole && approvalFilter !== 'pending') {
          setApprovalFilter('pending');
        }
        if (
          approvalFilter === 'pending' &&
          !['pending_admin', 'pending_superadmin'].includes(String(normalizedCreated.approvalStatus || ''))
        ) {
          setApprovalFilter('all');
        }
        const pendingStatus = String(normalizedCreated.approvalStatus || '');
        const apiMessage = res.data?.message;
        if (['pending_admin', 'pending_superadmin'].includes(pendingStatus)) {
          toast.info(apiMessage || 'Deal saved and pending approval. It will go live after approval.');
        } else {
          toast.success(apiMessage || 'Deal created successfully.');
        }
        closeCreateModal();
        return;
      }
      throw new Error('Failed to create deal');
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to create deal. Please try again.');
      const report = err?.response?.data?.data?.qualificationReport;
      if (report) setComboQualificationReport(report);
      toast.error(message, { autoClose: 6000 });
      setProductError(message);
      setDealActionFeedback({ type: 'error', message });
    } finally {
      setCreateDealLoading(false);
    }
  };

  const approveHomepagePlacement = async (id, approved) => {
    if (!id) return;
    setHomepageActionLoadingByDealId((prev) => ({ ...prev, [id]: approved ? 'approve' : 'reject' }));
    try {
      const paymentTerms = String(homepageTermsByDealId[id] || '').trim();
      if (approved && !paymentTerms) {
        toast.error('Enter payment terms before approving homepage placement.');
        return;
      }
      const res = await api.put(`/admin/deals/${id}/homepage-placement`, {
        approved,
        paymentTerms: approved ? paymentTerms : undefined,
        reason: approved ? undefined : 'Rejected by admin'
      });
      const updated = res.data?.data?.deal;
      if (updated) {
        setDeals((prev) =>
          prev.map((d) => (getDealId(d) === String(id) ? normalizeDealForUi({ ...d, ...updated }) : d))
        );
      }
      toast.success(res.data?.message || (approved ? 'Homepage placement approved' : 'Homepage placement rejected'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to update homepage placement'));
    } finally {
      setHomepageActionLoadingByDealId((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const reviewDeal = async (id, action) => {
    if (!id) return;
    setReviewLoadingByDealId((prev) => ({ ...prev, [id]: action }));
    setDealActionFeedback({ type: '', message: '' });
    try {
      const url = `/admin/deals/${id}/${action}`;
      const res = await api.put(url);
      const updated = res.data?.data?.deal;
      if (!updated) return;
      setDeals((prev) => prev.map((d) => {
        if (String(d._id || d.id) !== String(id)) return normalizeDealForUi(d);
        return normalizeDealForUi({ ...d, ...updated });
      }));
      const message = res.data?.message || (action === 'approve' ? 'Deal approved and set to Active' : 'Deal rejected');
      toast.success(message);
      setDealActionFeedback({ type: 'success', message });
    } catch (e) {
      const message = getApiErrorMessage(e, `Failed to ${action} deal`);
      toast.error(message);
      setProductError(message);
      setDealActionFeedback({ type: 'error', message });
    } finally {
      setReviewLoadingByDealId((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const openComboAnchorModal = async (dealId) => {
    if (!dealId) return;
    setComboAnchorModalDealId(dealId);
    setComboAnchorContext(null);
    setComboAnchorNewProductId('');
    setComboAnchorLoading(true);
    try {
      const res = await api.get(`/deals/${dealId}/combo/anchor-context`);
      setComboAnchorContext(res.data?.data || null);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load combo options.'));
      setComboAnchorModalDealId('');
    } finally {
      setComboAnchorLoading(false);
    }
  };

  const closeComboAnchorModal = () => {
    setComboAnchorModalDealId('');
    setComboAnchorContext(null);
    setComboAnchorNewProductId('');
  };

  const endComboAnchorDeal = async () => {
    const dealId = comboAnchorModalDealId;
    if (!dealId) return;
    setComboAnchorSubmitting(true);
    try {
      const res = await api.post(`/deals/${dealId}/combo/anchor-end`);
      const updated = res.data?.data?.deal;
      if (updated) {
        setDeals((prev) =>
          prev.map((d) => (getDealId(d) === dealId ? normalizeDealForUi({ ...d, ...updated }) : d))
        );
      }
      toast.success(res.data?.message || 'Combo bundle ended.');
      closeComboAnchorModal();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to end combo.'));
    } finally {
      setComboAnchorSubmitting(false);
    }
  };

  const continueComboAnchorDeal = async () => {
    const dealId = comboAnchorModalDealId;
    if (!dealId || !comboAnchorNewProductId) {
      toast.error('Select a replacement product for the bundle.');
      return;
    }
    setComboAnchorSubmitting(true);
    try {
      const res = await api.post(`/deals/${dealId}/combo/anchor-continue`, {
        newAnchorProductId: comboAnchorNewProductId
      });
      const updated = res.data?.data?.deal;
      if (updated) {
        setDeals((prev) =>
          prev.map((d) => (getDealId(d) === dealId ? normalizeDealForUi({ ...d, ...updated }) : d))
        );
      }
      toast.success(res.data?.message || 'Combo bundle updated and live again.');
      closeComboAnchorModal();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to continue combo.'));
    } finally {
      setComboAnchorSubmitting(false);
    }
  };

  const updateGarageDealStatus = async (deal, nextUiStatus) => {
    const dealId = getDealId(deal);
    if (!dealId) return;
    if (
      nextUiStatus === 'Active' &&
      normalizeRole(currentRole) === 'garage' &&
      !canGarageActivateDeal(deal)
    ) {
      const message = isDealApprovalPending(deal)
        ? `${GARAGE_ACTIVE_REQUIRES_ADMIN_MSG} Your deal is waiting for admin approval.`
        : GARAGE_ACTIVE_REQUIRES_ADMIN_MSG;
      toast.warning(message);
      setDealActionFeedback({ type: 'error', message });
      return;
    }
    setGarageStatusLoadingByDealId((prev) => ({ ...prev, [dealId]: true }));
    setDealActionFeedback({ type: '', message: '' });
    try {
      const res = await api.put(`/deals/${dealId}/status`, { status: nextUiStatus });
      const updated = res.data?.data?.deal;
      if (updated) {
        setDeals((prev) =>
          prev.map((d) => (getDealId(d) === dealId ? normalizeDealForUi({ ...d, ...updated }) : d))
        );
        toast.success(res.data?.message || 'Deal status updated');
      }
    } catch (e) {
      const message = getApiErrorMessage(e, 'Failed to update deal status');
      toast.error(message);
      setDealActionFeedback({ type: 'error', message });
    } finally {
      setGarageStatusLoadingByDealId((prev) => ({ ...prev, [dealId]: false }));
    }
  };

  const participateInDeal = async (deal) => {
    const dealId = getDealId(deal);
    if (!dealId) return;
    setDealActionFeedback({ type: '', message: '' });
    setParticipateLoadingByDealId((prev) => ({ ...prev, [dealId]: true }));
    try {
      const res = await api.post(`/deals/${dealId}/participate`, { quantity: 1 });
      const updated = res.data?.data?.deal;
      const message = res.data?.message || 'Participation saved';
      if (updated) {
        setDeals((prev) =>
          prev.map((d) => (getDealId(d) === dealId ? normalizeDealForUi({ ...d, ...updated }) : d))
        );
      }
      setParticipatedDealIds((prev) => ({ ...prev, [dealId]: true }));
      setDealActionFeedback({ type: 'success', message });
    } catch (e) {
      setDealActionFeedback({ type: 'error', message: getApiErrorMessage(e, 'Failed to participate in deal') });
    } finally {
      setParticipateLoadingByDealId((prev) => ({ ...prev, [dealId]: false }));
    }
  };

  const requestDeleteDeal = (deal) => {
    if (!deal || !getDealId(deal)) return;
    setDeleteConfirmDeal(deal);
  };

  const cancelDeleteDeal = () => {
    if (deleteConfirmDeal && deleteLoadingByDealId[getDealId(deleteConfirmDeal)]) return;
    setDeleteConfirmDeal(null);
  };

  const confirmDeleteDeal = async () => {
    const deal = deleteConfirmDeal;
    const dealId = getDealId(deal);
    if (!dealId) return;
    setDeleteLoadingByDealId((prev) => ({ ...prev, [dealId]: true }));
    setDealActionFeedback({ type: '', message: '' });
    try {
      const res = await api.delete(`/deals/${dealId}`);
      setDeals((prev) => prev.filter((d) => getDealId(d) !== dealId));
      if (viewDeal && getDealId(viewDeal) === dealId) setViewDeal(null);
      setDeleteConfirmDeal(null);
      const message = res?.data?.message || 'Deal deleted successfully';
      setDealActionFeedback({ type: 'success', message });
      toast.success(message);
    } catch (e) {
      const message = getApiErrorMessage(e, 'Failed to delete deal');
      setDealActionFeedback({ type: 'error', message });
      toast.error(message);
    } finally {
      setDeleteLoadingByDealId((prev) => ({ ...prev, [dealId]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Deals</h2>
          <p className="text-xs text-gray-600 mt-1">Create and manage deals shown to customers</p>
        </div>

        <div className="flex gap-2">
          {canCreateDeals && (
            <button
              type="button"
              onClick={openCreate}
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <FaPlus />
              Create Deal
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/admin/dashboard/marketing')}
            className="bg-white border border-slate-200 text-slate-700 rounded-lg px-4 py-2 flex items-center gap-2 text-sm font-medium hover:bg-slate-50 transition-colors"
            title="Go to Marketing campaigns"
          >
            <FaTag className="text-slate-500" />
            Campaigns
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md border border-gray-100 p-4">
        <div className="flex flex-wrap gap-2">
          {DEAL_CATEGORIES.map((c) => {
            const active = activeCategory === c.id;
            const count = counts[c.id] ?? (c.id === 'all' ? deals.length : 0);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveCategory(c.id)}
                className={
                  active
                    ? 'inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-xs sm:text-sm font-bold text-primary-700'
                    : 'inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs sm:text-sm font-semibold text-slate-600 hover:bg-slate-50'
                }
              >
                <span>{c.icon}</span>
                <span>{c.label}</span>
                <span className="rounded-full bg-black/5 px-1.5 py-0.5 text-[10px] tabular-nums">
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {activeCategory === 'combo' && isPlatformStaffRole && (
          <div className="mt-4 rounded-xl border border-rose-100 bg-rose-50/40 p-4">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="text-sm font-bold text-rose-900">Combo qualification failures</h3>
              <button
                type="button"
                onClick={loadComboFailures}
                disabled={comboFailuresLoading}
                className="text-xs font-bold text-rose-700 hover:underline disabled:opacity-50"
              >
                {comboFailuresLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
            {comboFailuresLoading && !comboFailures.length ? (
              <p className="text-xs text-slate-600">Loading failure log…</p>
            ) : comboFailures.length === 0 ? (
              <p className="text-xs text-slate-600">No recent combo validation rejections in audit log.</p>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-2">
                {comboFailures.map((row) => (
                  <div
                    key={row._id}
                    className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs text-slate-700"
                  >
                    <p className="font-semibold text-slate-900">
                      {row.meta?.message || row.action || 'combo_validation_reject'}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                      {row.actorId?.email ? ` · ${row.actorId.email}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setApprovalFilter('all')}
            className={
              approvalFilter === 'all'
                ? 'inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700'
                : 'inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50'
            }
          >
            All Approval States
          </button>
          <button
            type="button"
            onClick={() => setApprovalFilter('pending')}
            className={
              approvalFilter === 'pending'
                ? 'inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-700'
                : 'inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50'
            }
          >
            Pending Approvals
            <span className="rounded-full bg-black/5 px-1.5 py-0.5 text-[10px] tabular-nums">
              {deals.filter((d) => d.approvalStatus === 'pending_admin' || d.approvalStatus === 'pending_superadmin').length}
            </span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md border border-gray-100 p-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Deal Pool Commitment</h3>
            <p className="text-xs text-slate-600 mt-1">
              Admin sets this commitment value. Users can only view it while creating deals.
            </p>
          </div>
          <div className="w-full md:w-auto flex items-end gap-2">
            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                Commitment (%)
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={poolCommitmentLoading ? '' : poolCommitmentDraft}
                placeholder={poolCommitmentLoading ? 'Loading…' : '—'}
                onChange={(e) => setPoolCommitmentDraft(e.target.value)}
                disabled={!isPlatformStaffRole || poolCommitmentLoading}
                className="mt-1 w-36 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm disabled:bg-slate-50 disabled:text-slate-500"
              />
            </div>
            {isPlatformStaffRole && (
              <button
                type="button"
                onClick={saveDealPoolCommitment}
                disabled={poolCommitmentSaving || poolCommitmentLoading}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-60"
              >
                {poolCommitmentSaving ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>

      {activeCategory === 'clearance' && (isPlatformStaffRole || isSellerForClearance) && (
        <div className="bg-white rounded-lg shadow-md border border-amber-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-amber-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Clearance Deal Engine</h3>
              <p className="text-xs text-slate-600 mt-1">
                Slow movers (no sale in {clearanceRules.triggerDays ?? 45}d, sell rate ≤ {clearanceRules.sellRateThresholdPct ?? 10}% in last {clearanceRules.windowDays ?? 90}d) → seller accepts discount → live deal. Final sale, no returns, {clearanceRules.maxUnitsPerBuyer ?? 3} units max per buyer, 15 min reserve.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {isPlatformStaffRole && (
                <button
                  type="button"
                  onClick={runClearanceScan}
                  disabled={clearanceScanLoading}
                  className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 disabled:opacity-50"
                >
                  {clearanceScanLoading ? 'Scanning…' : 'Run eligibility scan'}
                </button>
              )}
              <button
                type="button"
                onClick={loadClearanceDrafts}
                disabled={clearanceDraftsLoading}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Refresh
              </button>
            </div>
          </div>
          {clearanceRules.lastScanAt && (
            <p className="px-5 py-2 text-[11px] text-slate-500 border-b border-slate-50">
              Last scan: {new Date(clearanceRules.lastScanAt).toLocaleString()}
              {clearanceRules.lastScanSummary && (
                <> — {clearanceRules.lastScanSummary.draftsCreated ?? 0} draft(s) from {clearanceRules.lastScanSummary.eligible ?? 0} eligible</>
              )}
            </p>
          )}
          {clearanceDraftsLoading ? (
            <p className="p-6 text-sm text-slate-500">Loading clearance suggestions…</p>
          ) : clearanceDrafts.length === 0 ? (
            <p className="p-6 text-sm text-slate-600">
              {isPlatformStaffRole
                ? 'No pending seller decisions. Run a scan to detect slow-moving products.'
                : 'No clearance suggestions right now. The platform will notify you when a product qualifies.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-amber-50/50">
                  <tr>
                    <th className="px-4 py-2 text-left text-[11px] font-bold text-slate-500 uppercase">Product</th>
                    <th className="px-4 py-2 text-left text-[11px] font-bold text-slate-500 uppercase">90d metrics</th>
                    <th className="px-4 py-2 text-left text-[11px] font-bold text-slate-500 uppercase">Last sale</th>
                    {isPlatformStaffRole && (
                      <th className="px-4 py-2 text-left text-[11px] font-bold text-slate-500 uppercase">Seller</th>
                    )}
                    <th className="px-4 py-2 text-left text-[11px] font-bold text-slate-500 uppercase">
                      {canManageClearanceDraft ? 'Your action' : 'Status'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {clearanceDrafts.map((draft) => {
                    const draftId = draft._id || draft.id;
                    const product = draft.productId || {};
                    const loading = clearanceActionLoading[draftId];
                    const minD = Number(clearanceRules.minDiscountPct) || 20;
                    const maxD = Number(clearanceRules.maxDiscountPct) || 70;
                    return (
                      <tr key={draftId} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm">
                          <span className="font-semibold text-slate-900">{product.name || '—'}</span>
                          <span className="block text-xs text-slate-500">Stock: {product.stock ?? '—'}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-700">
                          Sold: {draft.unitsSold90d ?? 0} / ref {draft.stockReference90d ?? '—'}
                          <span className="block font-semibold text-amber-800">Sell rate: {draft.sellRate90d ?? 0}%</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">
                          {draft.lastSaleDate
                            ? new Date(draft.lastSaleDate).toLocaleDateString()
                            : 'Never / unknown'}
                        </td>
                        {isPlatformStaffRole && (
                          <td className="px-4 py-3 text-xs text-slate-600">
                            {draft.vendorId?.name || draft.vendorId?.email || '—'}
                          </td>
                        )}
                        <td className="px-4 py-3">
                          {canManageClearanceDraft && draft.status === 'PENDING_SELLER_DECISION' ? (
                            <>
                              <div className="flex flex-wrap items-center gap-2">
                                <label className="text-[11px] text-slate-600">
                                  Discount % ({minD}–{maxD})
                                  <input
                                    type="number"
                                    min={minD}
                                    max={maxD}
                                    value={clearanceAcceptDiscount[draftId] ?? minD}
                                    onChange={(e) => setClearanceAcceptDiscount((prev) => ({
                                      ...prev,
                                      [draftId]: e.target.value
                                    }))}
                                    className="ml-1 w-16 border border-slate-200 rounded px-1 py-0.5 text-xs"
                                  />
                                </label>
                                <button
                                  type="button"
                                  disabled={!!loading}
                                  onClick={() => acceptClearanceDraft(draftId)}
                                  className="px-2 py-1 rounded bg-emerald-600 text-white text-xs font-bold disabled:opacity-50"
                                >
                                  {loading === 'accept' ? '…' : 'Accept'}
                                </button>
                                <button
                                  type="button"
                                  disabled={!!loading}
                                  onClick={() => rejectClearanceDraft(draftId)}
                                  className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-xs font-bold disabled:opacity-50"
                                >
                                  {loading === 'reject' ? '…' : 'Reject'}
                                </button>
                              </div>
                              <p className="text-[10px] text-red-600 mt-1">Final sale — no returns at checkout</p>
                            </>
                          ) : (
                            <span className="text-xs font-semibold text-slate-500">{draft.status}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900">Deals</h3>
            <span className={sellerFloorBadgeClass}>
              Seller floor: {sellerFloorPct}%
            </span>
          </div>
          <p className="text-xs text-slate-500">{filteredDeals.length} shown</p>
        </div>
        {!!dealActionFeedback.message && (
          <div className={`px-5 py-3 text-xs font-semibold border-b ${
            dealActionFeedback.type === 'error'
              ? 'bg-red-50 text-red-700 border-red-100'
              : 'bg-emerald-50 text-emerald-700 border-emerald-100'
          }`}>
            {dealActionFeedback.message}
          </div>
        )}

        {pausedComboDeals.length > 0 && (
          <div className="mx-5 mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-bold">Combo bundle paused — first product sold out</p>
            <p className="text-xs mt-1 text-amber-800">
              {pausedComboDeals.length} combo deal(s) are hidden from buyers. End the deal or pick a new first product; other items in the bundle stay the same.
            </p>
          </div>
        )}

        {filteredDeals.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-sm font-semibold text-slate-700">No deals in this category yet.</p>
            <p className="text-xs text-slate-500 mt-1">Click “Create Deal” and pick a category.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full deals-list-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Title</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Spare part</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Category</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Discount</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Deal Pool</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Coupon</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Approval</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-5 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500 w-[1%] whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredDeals.map((d) => (
                  (() => {
                    const stock = d.productId ? stockByProductId[d.productId] : null;
                    const over = String(d.category) !== 'garage_service' && (stock === 0 || stock < 0);
                    const rawStatus = String(d.status || '');
                    const shownStatus = over
                      ? 'Over'
                      : rawStatus === 'Inactive' || rawStatus === 'Draft'
                        ? 'Draft'
                        : rawStatus === 'Live'
                          ? 'Active'
                          : rawStatus;
                    const clearanceReason = (() => {
                      if (String(d.category) !== 'clearance') return '';
                      if (shownStatus !== 'Over') return '';
                      const endTs = d.endDate ? new Date(d.endDate).getTime() : 0;
                      if (endTs > 0 && endTs <= Date.now()) return 'Expired';
                      if (Number(d.poolRemainingUnits || 0) <= 0) return 'Pool depleted';
                      return 'Completed';
                    })();
                    return (
                  <tr
                    key={d.id || d._id || `${d.title}-${d.createdAt || ''}`}
                    className="bg-white hover:bg-slate-50"
                  >
                    <td className="px-5 py-4 text-sm font-semibold text-slate-900">{d.title}</td>
                    <td className="px-5 py-4 text-sm text-slate-700">
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-900">
                          {String(d.category) === 'garage_service'
                            ? (d?.garageServiceConfig?.serviceType || 'Service')
                            : (d.productName || '—')}
                        </span>
                        <span className="text-xs text-slate-500">
                          {d.productPrice != null ? formatCurrency(d.productPrice) : '—'}
                        </span>
                        <span className={`text-[11px] font-semibold mt-0.5 ${over ? 'text-red-600' : 'text-slate-500'}`}>
                          Stock: {stock == null ? '—' : stock}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-700">
                      <div className="flex items-center gap-2">
                        <span>{DEAL_CATEGORIES.find((c) => c.id === d.category)?.label || d.category}</span>
                        {String(d.category) === 'flash' && (
                          <span className={sellerFloorRowBadgeClass}>
                            Floor {sellerFloorPct}%
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm font-semibold text-emerald-700">
                      {String(d.category) === 'garage_service' && d?.garageBooking?.fullyBooked ? (
                        <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-[11px] font-bold text-amber-800">
                          Fully Booked
                        </span>
                      ) : String(d.category) === 'coupon' ? (
                        formatCouponDiscountLabel(d)
                      ) : String(d.category) === 'garage_service' ? (
                        formatGarageServiceDiscountLabel(d)
                      ) : (
                        formatDealDiscountPctAndAmount(d)
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-700">
                      <div className="flex flex-col">
                        {String(d.category) === 'garage_service' ? (
                          <span className="font-semibold text-slate-900">Booking-based</span>
                        ) : (
                          <>
                            <span className="font-semibold text-slate-900">
                              {d.poolRemainingUnits ?? 0}/{d.poolUnits ?? 0}
                            </span>
                            <span className="text-xs text-slate-500">
                              {d.commitmentPercentage ?? 20}% committed
                            </span>
                          </>
                        )}
                        {String(d.category) === 'brand' && (
                          <span className="text-[11px] text-indigo-700 font-semibold">
                            Budget: {formatCurrency(Number(d?.brandConfig?.budgetUsed || 0))} / {formatCurrency(Number(d?.brandConfig?.budgetTotal || 0))}
                          </span>
                        )}
                        {String(d.category) === 'garage_service' && (
                          <span className="text-[11px] text-indigo-700 font-semibold">
                            {d?.garageServiceConfig?.serviceType || 'Service'}
                            {d?.garageServiceConfig?.isPlatformCampaign && (() => {
                              const cities = Array.isArray(d?.garageServiceConfig?.cities) && d.garageServiceConfig.cities.length
                                ? d.garageServiceConfig.cities
                                : d?.garageServiceConfig?.city
                                  ? [d.garageServiceConfig.city]
                                  : [];
                              return cities.length ? ` · ${cities.join(', ')}` : '';
                            })()}
                            {Array.isArray(d?.garageServiceConfig?.serviceAreaPincodes) &&
                            d.garageServiceConfig.serviceAreaPincodes.length > 0
                              ? ` · PIN ${d.garageServiceConfig.serviceAreaPincodes.join(', ')}`
                              : ''}
                            {d?.garageServiceConfig?.isPlatformCampaign ? ' · Platform campaign' : ''}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-700">
                      {String(d.category) !== 'coupon' ? (
                        <span className="text-xs text-slate-400">—</span>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {(() => {
                            const used = Math.max(0, Number(d?.couponConfig?.usageCount || 0));
                            const limit = Math.max(0, Number(d?.couponConfig?.totalUsageLimit || 0));
                            const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                            return (
                              <>
                          <span className="text-xs font-bold text-slate-900">
                            {d?.couponConfig?.code || 'NO-CODE'}
                          </span>
                          <span className="text-[11px] text-slate-600">
                            Used: {Number(d?.couponConfig?.usageCount || 0)}
                            {Number(d?.couponConfig?.totalUsageLimit || 0) > 0
                              ? ` / ${Number(d.couponConfig.totalUsageLimit)}`
                              : ' / unlimited'}
                          </span>
                          <div className="flex flex-wrap items-center gap-1">
                            {!!d?.couponConfig?.firstOrderOnly && (
                              <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700">
                                First order only
                              </span>
                            )}
                            {Number(d?.couponConfig?.usageLimitPerUser || 0) > 0 && (
                              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold text-slate-700">
                                Per user: {Number(d.couponConfig.usageLimitPerUser)}
                              </span>
                            )}
                          </div>
                          {limit > 0 && (
                            <div className="mt-1">
                              <div className="h-1.5 w-28 rounded-full bg-slate-100 overflow-hidden">
                                <div
                                  className={`h-full ${pct >= 90 ? 'bg-red-500' : pct >= 60 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-slate-500">{pct}% used</span>
                            </div>
                          )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-700">
                      <div className="flex flex-col gap-1">
                        <span className={`text-xs font-semibold ${
                          d.approvalStatus === 'pending_superadmin' || d.approvalStatus === 'pending_admin'
                            ? 'text-amber-700'
                            : d.approvalStatus === 'approved'
                              ? 'text-emerald-700'
                              : d.approvalStatus === 'rejected'
                                ? 'text-red-700'
                                : 'text-slate-600'
                        }`}>
                          {formatApprovalStatusLabel(d)}
                        </span>
                        {isDealApprovalPending(d) && (
                          <span className="text-[10px] font-bold text-amber-800">
                            Discount above 40% — approval required
                          </span>
                        )}
                        {isHomepagePlacementPending(d) && isPlatformStaffRole && (
                          <div className="mt-2 p-2 rounded-lg border border-violet-200 bg-violet-50 space-y-2">
                            <span className="text-[10px] font-bold text-violet-900 block">
                              Homepage placement — set payment terms and approve
                            </span>
                            <input
                              type="text"
                              placeholder="Payment terms (e.g. ₹500/day for 7 days)"
                              value={homepageTermsByDealId[getDealId(d)] || ''}
                              onChange={(e) =>
                                setHomepageTermsByDealId((prev) => ({
                                  ...prev,
                                  [getDealId(d)]: e.target.value
                                }))
                              }
                              className="w-full px-2 py-1.5 text-xs rounded border border-violet-200"
                            />
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => approveHomepagePlacement(getDealId(d), true)}
                                disabled={!!homepageActionLoadingByDealId[getDealId(d)]}
                                className="px-2 py-1 rounded bg-violet-700 text-white text-[10px] font-bold disabled:opacity-50"
                              >
                                {homepageActionLoadingByDealId[getDealId(d)] === 'approve'
                                  ? 'Saving…'
                                  : 'Approve homepage'}
                              </button>
                              <button
                                type="button"
                                onClick={() => approveHomepagePlacement(getDealId(d), false)}
                                disabled={!!homepageActionLoadingByDealId[getDealId(d)]}
                                className="px-2 py-1 rounded bg-white border border-violet-300 text-violet-900 text-[10px] font-bold disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </div>
                          </div>
                        )}
                        {!isHomepagePlacementPending(d) && d?.homepagePlacement?.requested && (
                          <span className="text-[10px] text-violet-700 font-medium">
                            {formatHomepagePlacementLabel(d)}
                          </span>
                        )}
                        {canReviewDeal(d) && (
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <button
                              type="button"
                              onClick={() => reviewDeal(getDealId(d), 'approve')}
                              disabled={!!reviewLoadingByDealId[getDealId(d)]}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50"
                            >
                              <FaCheckCircle className="text-[11px]" aria-hidden />
                              {reviewLoadingByDealId[getDealId(d)] === 'approve' ? 'Approving…' : 'Approve'}
                            </button>
                            <button
                              type="button"
                              onClick={() => reviewDeal(getDealId(d), 'reject')}
                              disabled={!!reviewLoadingByDealId[getDealId(d)]}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700 disabled:opacity-50"
                            >
                              <FaTimesCircle className="text-[11px]" aria-hidden />
                              {reviewLoadingByDealId[getDealId(d)] === 'reject' ? 'Rejecting…' : 'Reject'}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className={`px-5 py-4 text-sm font-semibold ${shownStatus === 'Over' ? 'text-red-600' : 'text-slate-700'}`}>
                      <div className="flex flex-col gap-1">
                        {String(d.category) === 'garage_service' &&
                        normalizeRole(currentRole) === 'garage' &&
                        !isComboAnchorPaused(d) ? (
                          <select
                            value={shownStatus === 'Active' ? 'Active' : shownStatus === 'Scheduled' ? 'Scheduled' : 'Draft'}
                            disabled={!!garageStatusLoadingByDealId[getDealId(d)]}
                            onChange={(e) => updateGarageDealStatus(d, e.target.value)}
                            className="w-full max-w-[120px] px-2 py-1 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-800"
                          >
                            {STANDARD_DEAL_STATUS_OPTIONS.map((opt) => (
                              <option
                                key={opt.value}
                                value={opt.value}
                                disabled={opt.value === 'Active' && !canGarageActivateDeal(d)}
                              >
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span>{isComboAnchorPaused(d) ? 'Paused' : shownStatus}</span>
                        )}
                        {isComboAnchorPaused(d) && (
                          <>
                            <span className="text-[11px] font-semibold text-amber-700">
                              Anchor out of stock — not visible to buyers
                            </span>
                            {isPlatformStaffRole && (
                              <button
                                type="button"
                                onClick={() => openComboAnchorModal(getDealId(d))}
                                className="mt-1 w-fit px-2 py-1 rounded bg-amber-100 text-amber-900 text-[11px] font-bold hover:bg-amber-200"
                              >
                                End or replace product
                              </button>
                            )}
                          </>
                        )}
                        {clearanceReason && (
                          <span className="text-[11px] font-semibold text-amber-700">{clearanceReason}</span>
                        )}
                        {String(d.category) === 'garage_service' &&
                          normalizeRole(currentRole) === 'garage' &&
                          isDealApprovalPending(d) && (
                          <span className="text-[11px] font-semibold text-amber-700">
                            Awaiting admin approval — becomes Active automatically when approved
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-700 align-middle">
                      <div
                        className="flex items-center justify-end gap-1 flex-nowrap"
                        role="group"
                        aria-label="Deal actions"
                      >
                        <button
                          type="button"
                          onClick={() => setViewDeal(d)}
                          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-emerald-600 hover:bg-emerald-50 transition"
                          title="View deal"
                          aria-label="View deal"
                        >
                          <FaEye className="text-sm" />
                        </button>
                        {isPlatformStaffRole && canReviewDeal(d) && (
                          <>
                            <button
                              type="button"
                              onClick={() => reviewDeal(getDealId(d), 'approve')}
                              disabled={!!reviewLoadingByDealId[getDealId(d)]}
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-emerald-600 hover:bg-emerald-50 transition disabled:opacity-50"
                              title="Approve deal"
                              aria-label="Approve deal"
                            >
                              <FaCheckCircle className="text-sm" />
                            </button>
                            <button
                              type="button"
                              onClick={() => reviewDeal(getDealId(d), 'reject')}
                              disabled={!!reviewLoadingByDealId[getDealId(d)]}
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                              title="Reject deal"
                              aria-label="Reject deal"
                            >
                              <FaTimesCircle className="text-sm" />
                            </button>
                          </>
                        )}
                        {canShowParticipateButton(d) && (() => {
                          const dealId = getDealId(d);
                          const isLoading = !!participateLoadingByDealId[dealId];
                          const alreadyParticipated =
                            !!participatedDealIds[dealId] || hasUserParticipated(d, currentUserId);
                          const disabled =
                            isLoading || alreadyParticipated || shownStatus === 'Over';
                          return (
                            <button
                              type="button"
                              onClick={() => participateInDeal(d)}
                              disabled={disabled}
                              className={`inline-flex h-8 shrink-0 items-center justify-center rounded-lg border px-2.5 text-[11px] font-bold whitespace-nowrap ${
                                disabled
                                  ? alreadyParticipated
                                    ? 'border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed'
                                    : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                                  : 'border-primary-600 bg-primary-600 text-white hover:bg-primary-700'
                              }`}
                              title={
                                alreadyParticipated
                                  ? 'Already joined'
                                  : shownStatus === 'Over'
                                    ? 'Deal is over'
                                    : 'Join this deal pool'
                              }
                            >
                              {isLoading ? '…' : alreadyParticipated ? 'Joined' : 'Join'}
                            </button>
                          );
                        })()}
                        {canDeleteDeal(d) && (() => {
                          const dealId = getDealId(d);
                          const deleting = !!deleteLoadingByDealId[dealId];
                          return (
                            <button
                              type="button"
                              onClick={() => requestDeleteDeal(d)}
                              disabled={deleting}
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-red-600 hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                              title={deleting ? 'Deleting…' : 'Delete deal'}
                              aria-label={deleting ? 'Deleting deal' : 'Delete deal'}
                            >
                              <FaTrash className="text-sm" />
                            </button>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                    );
                  })()
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleteConfirmDeal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center px-4 bg-black/40 backdrop-blur-[2px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-deal-dialog-title"
          onClick={cancelDeleteDeal}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md shadow-[0_24px_60px_rgba(15,23,42,0.25)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
                  <FaTrash className="text-lg" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 id="delete-deal-dialog-title" className="text-lg font-bold text-slate-900">
                    Delete deal?
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    <span className="font-semibold text-slate-800">
                      {deleteConfirmDeal.title || 'Untitled deal'}
                    </span>{' '}
                    will be permanently removed. This cannot be undone.
                  </p>
                  {deleteConfirmDeal.productId && (
                    <p className="mt-2 text-xs text-slate-500">
                      Spare part:{' '}
                      <span className="font-medium text-slate-700">
                        {typeof deleteConfirmDeal.productId === 'object'
                          ? getProductName(deleteConfirmDeal.productId)
                          : 'Linked product'}
                      </span>
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <button
                  type="button"
                  onClick={cancelDeleteDeal}
                  disabled={!!deleteLoadingByDealId[getDealId(deleteConfirmDeal)]}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteDeal}
                  disabled={!!deleteLoadingByDealId[getDealId(deleteConfirmDeal)]}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-red-600 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoadingByDealId[getDealId(deleteConfirmDeal)] ? 'Deleting…' : 'Delete deal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewDeal && (
        <div className="fixed inset-0 z-[9998] flex items-start justify-center px-3 sm:px-4 pt-6 sm:pt-8 pb-4 overflow-y-auto bg-black/20 backdrop-blur-[2px]">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden shadow-[0_24px_60px_rgba(15,23,42,0.20)]">
            <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-base font-bold text-slate-900">Deal details</h3>
                <p className="text-xs text-slate-500 mt-0.5">{viewDeal.title || 'Untitled deal'}</p>
              </div>
              <button
                type="button"
                onClick={() => setViewDeal(null)}
                className="p-2 rounded-lg hover:bg-slate-50 text-slate-600"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-4 overflow-y-auto max-h-[calc(92vh-80px)]">
              {(() => {
                const cat = String(viewDeal.category || '');
                const cfg = viewDeal.garageServiceConfig || {};
                const coupon = viewDeal.couponConfig || {};
                const brand = viewDeal.brandConfig || {};
                const stock = viewDeal.productId ? stockByProductId[viewDeal.productId] : null;
                const over =
                  cat !== 'garage_service' && viewDeal.productId && (stock === 0 || stock < 0);
                const statusLabel = getShownDealStatus(viewDeal, over);
                const discountLabel =
                  cat === 'coupon'
                    ? formatCouponDiscountLabel(viewDeal)
                    : cat === 'garage_service'
                      ? formatGarageServiceDiscountLabel(viewDeal)
                          : formatDealDiscountPctAndAmount(viewDeal);
                const Detail = ({ label, value }) => (
                  <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2.5">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
                    <p className="text-sm font-semibold text-slate-900 mt-0.5 break-words">{value ?? '—'}</p>
                  </div>
                );
                return (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Detail label="Deal type" value={DEAL_CATEGORIES.find((c) => c.id === cat)?.label || cat} />
                      <Detail label="Status" value={isComboAnchorPaused(viewDeal) ? 'Paused' : statusLabel} />
                      <Detail label="Approval" value={viewDeal.approvalStatus || 'not_required'} />
                      <Detail label="Homepage placement" value={formatHomepagePlacementLabel(viewDeal)} />
                      <Detail
                        label="Return policy (locked)"
                        value={viewDeal.returnPolicy?.policySummary || 'Standard policy applies'}
                      />
                      <Detail label="Discount" value={discountLabel} />
                      <Detail label="Start" value={formatDealDateTime(viewDeal.startDate)} />
                      <Detail label="End" value={formatDealDateTime(viewDeal.endDate)} />
                    </div>
                    {cat === 'garage_service' && viewDeal?.garageBooking?.fullyBooked && (
                      <p className="text-sm font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        Fully Booked for today — new bookings will be rejected until capacity opens.
                      </p>
                    )}
                    {cat === 'garage_service' && (
                      <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-4 space-y-3">
                        <p className="text-xs font-bold text-indigo-800 uppercase tracking-wider">Garage service</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Detail label="Service" value={cfg.serviceType || '—'} />
                          {cfg.isPlatformCampaign && (
                            <Detail
                              label="Cities"
                              value={
                                Array.isArray(cfg.cities) && cfg.cities.length
                                  ? cfg.cities.join(', ')
                                  : cfg.city || '—'
                              }
                            />
                          )}
                          {cfg.isPlatformCampaign && (
                            <Detail
                              label="Pincodes"
                              value={
                                Array.isArray(cfg.serviceAreaPincodes) && cfg.serviceAreaPincodes.length
                                  ? cfg.serviceAreaPincodes.join(', ')
                                  : 'All pincodes in city'
                              }
                            />
                          )}
                          {cfg.isPlatformCampaign && (
                            <Detail label="Campaign type" value="Platform — garages in selected cities can join" />
                          )}
                          <Detail
                            label="Vehicle number required"
                            value={cfg.requiresVehicleNumber ? 'Yes' : 'No'}
                          />
                        </div>
                        <p className="text-[11px] text-indigo-800">
                          {statusLabel === 'Active'
                            ? 'Visible on home garage cards (best discount shown).'
                            : 'Draft — hidden from home page until set to Active.'}
                        </p>
                      </div>
                    )}
                    {cat === 'coupon' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Detail label="Coupon code" value={coupon.code || '—'} />
                        <Detail label="Min order" value={formatCurrency(coupon.minOrderValue)} />
                        <Detail label="Usage" value={`${coupon.usageCount ?? 0}${coupon.totalUsageLimit ? ` / ${coupon.totalUsageLimit}` : ' / unlimited'}`} />
                        <Detail label="Per user limit" value={coupon.usageLimitPerUser || '—'} />
                      </div>
                    )}
                    {cat === 'brand' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Detail label="Brand" value={brand.campaignBrand || brand.filterBrand || '—'} />
                        <Detail
                          label="Budget"
                          value={`${formatCurrency(Number(brand.budgetUsed || 0))} / ${formatCurrency(Number(brand.budgetTotal || 0))}`}
                        />
                      </div>
                    )}
                    {cat !== 'garage_service' && cat !== 'coupon' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Detail
                          label="Product"
                          value={viewDeal.productName || '—'}
                        />
                        <Detail label="Deal pool" value={`${viewDeal.poolRemainingUnits ?? 0} / ${viewDeal.poolUnits ?? 0} units`} />
                        <Detail label="Commitment" value={`${viewDeal.commitmentPercentage ?? 20}%`} />
                        <Detail label="Stock" value={stock == null ? '—' : String(stock)} />
                      </div>
                    )}
                    {viewDeal.scheduledGoLiveAt && (
                      <Detail label="Scheduled go-live" value={formatDealDateTime(viewDeal.scheduledGoLiveAt)} />
                    )}
                  </>
                );
              })()}
            </div>
            <div className="p-4 sm:p-5 border-t border-slate-100 flex justify-end">
              <button
                type="button"
                onClick={() => setViewDeal(null)}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {comboAnchorModalDealId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-bold text-slate-900">Combo — first product sold out</h3>
              <button
                type="button"
                onClick={closeComboAnchorModal}
                className="p-2 rounded-lg hover:bg-slate-50 text-slate-600"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {comboAnchorLoading ? (
                <p className="text-sm text-slate-600">Loading options…</p>
              ) : (
                <>
                  <p className="text-sm text-slate-700">
                    The first item in this bundle has no stock left. Buyers cannot see this deal until you choose an action.
                  </p>
                  {comboAnchorContext?.anchorProduct && (
                    <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm">
                      <span className="font-semibold text-red-900">Sold out: </span>
                      {comboAnchorContext.anchorProduct.name}
                    </div>
                  )}
                  {(comboAnchorContext?.companions || []).length > 0 && (
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm">
                      <p className="font-semibold text-emerald-900 mb-1">Kept in bundle (unchanged):</p>
                      <ul className="list-disc list-inside text-emerald-800 text-xs space-y-0.5">
                        {comboAnchorContext.companions.map((p) => (
                          <li key={p._id}>{p.name} — stock {p.stock ?? 0}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
                      New first product (same seller)
                    </label>
                    <select
                      value={comboAnchorNewProductId}
                      onChange={(e) => setComboAnchorNewProductId(e.target.value)}
                      className="mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200"
                    >
                      <option value="">Select replacement product</option>
                      {(comboAnchorContext?.replacementCandidates || []).map((p) => (
                        <option key={p._id} value={p._id}>
                          {p.name} — {formatCurrency(p.price)} (stock {p.stock ?? 0})
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-slate-500 mt-1">
                      Discount {comboAnchorContext?.discountPct ?? 0}% applies to the new total price; pool is recalculated from weakest stock.
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className="p-5 border-t border-slate-100 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeComboAnchorModal}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50"
                disabled={comboAnchorSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={endComboAnchorDeal}
                className="px-4 py-2 rounded-xl border border-red-200 bg-red-50 text-red-700 font-semibold hover:bg-red-100 disabled:opacity-60"
                disabled={comboAnchorSubmitting || comboAnchorLoading}
              >
                End deal
              </button>
              <button
                type="button"
                onClick={continueComboAnchorDeal}
                className="btn-primary px-5 py-2 rounded-xl text-sm disabled:opacity-60"
                disabled={comboAnchorSubmitting || comboAnchorLoading || !comboAnchorNewProductId}
              >
                {comboAnchorSubmitting ? 'Saving…' : 'Continue with new product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-[9999] flex items-start justify-center px-3 sm:px-4 pt-6 sm:pt-8 pb-4 overflow-y-auto bg-black/5 backdrop-blur-[2px]">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden shadow-[0_24px_60px_rgba(15,23,42,0.20),0_10px_24px_rgba(15,23,42,0.12)]">
            <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-base font-bold text-slate-900">
                {pendingClearanceDraftId ? 'Accept clearance suggestion' : 'Create Deal'}
              </h3>
              <button
                type="button"
                onClick={closeCreateModal}
                className="p-2 rounded-lg hover:bg-slate-50 text-slate-600"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            <form
              noValidate
              onSubmit={saveDeal}
              className={`deal-create-form p-4 sm:p-5 space-y-4 overflow-y-auto max-h-[calc(96vh-118px)] md:max-h-[calc(92vh-124px)]${createFieldErrors.length ? ' deal-create-form--has-errors' : ''}`}
            >
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Deal type<RequiredMark /></label>
                <select
                  value={form.category}
                  disabled={!!pendingClearanceDraftId}
                  onChange={(e) => {
                    const nextCategory = e.target.value;
                    if (pendingClearanceDraftId) return;
                    setSelectedProduct(null);
                    setProductError('');
                    clearCreateFieldValidation();
                    setForm((p) => {
                      const toFlashBrand = nextCategory === 'flash' || nextCategory === 'brand';
                      const fromFlashBrand = p.category === 'flash' || p.category === 'brand';
                      let nextStatus = p.status;
                      if (toFlashBrand && !fromFlashBrand) {
                        if (p.status === 'Active') nextStatus = 'Live';
                        else if (p.status === 'Inactive') nextStatus = 'Draft';
                        else if (!['Draft', 'Scheduled', 'Live'].includes(p.status)) nextStatus = 'Draft';
                      } else                       if (!toFlashBrand && fromFlashBrand) {
                        if (p.status === 'Live') nextStatus = 'Active';
                        else if (!['Draft', 'Active', 'Scheduled'].includes(p.status)) nextStatus = 'Draft';
                      }
                      const nextDiscount = nextCategory === 'clearance'
                        ? (Number(clearanceRules.minDiscountPct) || 20)
                        : 0;
                      return {
                        ...p,
                        category: nextCategory,
                        status: nextStatus,
                        discountType: 'percent',
                        discountAmount: 0,
                        discountPct: nextDiscount,
                        brandProductId: '',
                        comboDiscountPct: 0,
                        productPickerBrand: '',
                        productPickerCategory: '',
                        sellerOwnSellerId: nextCategory === 'seller_own' ? p.sellerOwnSellerId : ''
                      };
                    });
                    loadProductOptions(nextCategory);
                  }}
                  className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-200', 'category')}
                  {...getCreateFieldProps('category')}
                >
                  {creatableCategories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
                {renderCreateFieldError('category')}
                <p className="text-[11px] text-slate-500 mt-1">
                  Your role: <span className="font-semibold">{currentRole}</span>
                </p>
              </div>

              {isFlashDealForm && (
                <div className="space-y-4 rounded-xl border border-amber-200 bg-amber-50/40 p-4">
                  <p className="text-xs font-bold text-amber-800 uppercase tracking-wider">
                    Flash sale — 6 admin inputs (system handles pool, routing, fraud rules)
                  </p>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
                      Campaign name<RequiredMark /> <span className="font-normal normal-case text-slate-500">(internal only)</span>
                    </label>
                    <input
                      value={form.title}
                      onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                      className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'title')}
                      placeholder="e.g., Weekend Engine Oil Flash"
                      {...getCreateFieldProps('title')}
                    />
                    {renderCreateFieldError('title')}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">What&apos;s on sale<RequiredMark /></label>
                      <select
                        value={form.flashSelectionType}
                        onChange={(e) => setForm((p) => ({
                          ...p,
                          flashSelectionType: e.target.value,
                          flashFilterProductId: ''
                        }))}
                        className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'flashSelectionType')}
                        {...getCreateFieldProps('flashSelectionType')}
                      >
                        <option value="category">Category</option>
                        <option value="brand">Brand</option>
                        <option value="sku">Specific SKU(s)</option>
                      </select>
                      {renderCreateFieldError('flashSelectionType')}
                    </div>
                    <div>
                      {form.flashSelectionType === 'category' && (
                        <>
                          <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Category<RequiredMark /></label>
                          <input
                            list="flash-category-list"
                            value={form.flashFilterCategory}
                            onChange={(e) => setForm((p) => ({ ...p, flashFilterCategory: e.target.value }))}
                            className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'flashFilterCategory')}
                            {...getCreateFieldProps('flashFilterCategory')}
                          />
                          {renderCreateFieldError('flashFilterCategory')}
                          <datalist id="flash-category-list">
                            {couponScopeCategoryOptions.map((c) => (
                              <option key={c} value={c} />
                            ))}
                          </datalist>
                        </>
                      )}
                      {form.flashSelectionType === 'brand' && (
                        <>
                          <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Brand<RequiredMark /></label>
                          <select
                            value={form.flashFilterBrand}
                            onChange={(e) => setForm((p) => ({
                              ...p,
                              flashFilterBrand: e.target.value,
                              flashFilterProductId: ''
                            }))}
                            className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'flashFilterBrand')}
                            {...getCreateFieldProps('flashFilterBrand')}
                          >
                            <option value="">Select brand</option>
                            {brandOptions.map((b) => (
                              <option key={b} value={b}>{b}</option>
                            ))}
                          </select>
                          {renderCreateFieldError('flashFilterBrand')}
                        </>
                      )}
                      {form.flashSelectionType === 'sku' && (
                        <>
                          <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">SKU<RequiredMark /></label>
                          <input
                            value={form.flashFilterSku}
                            onChange={(e) => setForm((p) => ({ ...p, flashFilterSku: e.target.value }))}
                            className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'flashFilterSku')}
                            placeholder="Product SKU"
                            {...getCreateFieldProps('flashFilterSku')}
                          />
                          {renderCreateFieldError('flashFilterSku')}
                        </>
                      )}
                    </div>
                  </div>
                  {form.flashSelectionType === 'brand' && (
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Spare part<RequiredMark /></label>
                      <select
                        value={form.flashFilterProductId || ''}
                        onChange={(e) => setForm((p) => ({ ...p, flashFilterProductId: e.target.value }))}
                        className={withCreateFieldError('mt-2 w-full max-w-[220px] px-2.5 py-1.5 text-sm rounded-xl bg-white border border-slate-200', 'flashFilterProductId')}
                        disabled={!form.flashFilterBrand || productOptionsLoading}
                        {...getCreateFieldProps('flashFilterProductId')}
                      >
                        <option value="">
                          {!form.flashFilterBrand
                            ? 'Select a brand first'
                            : productOptionsLoading
                              ? 'Loading spare parts…'
                              : flashBrandProductOptions.length === 0
                                ? 'No in-stock parts for this brand'
                                : 'Select spare part'}
                        </option>
                        {flashBrandProductOptions.map((p) => {
                          const id = getProductId(p);
                          return (
                            <option key={id} value={id}>
                              {formatProductListingLabel(p)}
                            </option>
                          );
                        })}
                      </select>
                      {renderCreateFieldError('flashFilterProductId')}
                      <p className="text-[11px] text-slate-500 mt-1">
                        Anchor product for this flash sale (in-stock parts for the selected brand).
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount type</label>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                        <input
                          type="radio"
                          name="flashDiscountType"
                          checked={form.discountType === 'percent'}
                          onChange={() => setForm((p) => ({ ...p, discountType: 'percent' }))}
                        />
                        <span className="font-semibold">Percentage (%)</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                        <input
                          type="radio"
                          name="flashDiscountType"
                          checked={form.discountType === 'amount'}
                          onChange={() => setForm((p) => ({ ...p, discountType: 'amount' }))}
                        />
                        <span className="font-semibold">Fixed amount (₹)</span>
                      </label>
                    </div>
                    {form.discountType === 'percent' ? (
                      <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount (%)<RequiredMark /></label>
                        <input
                          type="number"
                          min="5"
                          max="70"
                          value={form.discountPct}
                          onChange={(e) => {
                            const nextPct = Math.max(5, Math.min(70, Number(e.target.value) || 0));
                            const base = getDiscountBaseForCurrentDealType();
                            const nextAmt = base > 0 ? Number(((base * nextPct) / 100).toFixed(2)) : 0;
                            setForm((p) => ({ ...p, discountPct: e.target.value, discountAmount: nextAmt }));
                          }}
                          className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'discountPct')}
                          {...getCreateFieldProps('discountPct')}
                        />
                        {renderCreateFieldError('discountPct')}
                        <p className="text-[11px] text-slate-600 mt-1">
                          Discount amount: <span className="font-semibold">{form.discountAmount ? formatCurrency(form.discountAmount) : '—'}</span> off
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount amount (₹)<RequiredMark /></label>
                        <input
                          type="number"
                          min="1"
                          value={form.discountAmount}
                          onChange={(e) => {
                            const nextAmt = Math.max(0, Number(e.target.value) || 0);
                            const base = getDiscountBaseForCurrentDealType();
                            const nextPct = base > 0 ? Number(((nextAmt / base) * 100).toFixed(2)) : 0;
                            setForm((p) => ({ ...p, discountAmount: e.target.value, discountPct: nextPct }));
                          }}
                          className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'discountPct')}
                          {...getCreateFieldProps('discountPct')}
                        />
                        {renderCreateFieldError('discountPct')}
                        <p className="text-[11px] text-slate-600 mt-1">
                          Discount (%): <span className="font-semibold">{Number(form.discountPct || 0).toFixed(2)}%</span>
                        </p>
                      </div>
                    )}
                    <p className="text-[11px] text-amber-700 mt-1">{getDiscountApprovalHint()}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Status<RequiredMark /></label>
                    <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'status')} {...getCreateFieldProps('status')}>
                      <option value="Draft">Draft</option>
                      <option value="Scheduled">Scheduled</option>
                      <option value="Live">Live</option>
                    </select>
                    {renderCreateFieldError('status')}
                    {form.status === 'Draft' && (
                      <p className="text-[11px] text-slate-500 mt-1">Draft deals are not shown on the website.</p>
                    )}
                  </div>
                  {renderScheduledGoLiveFields()}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Campaign start<RequiredMark /></label>
                      <div className="mt-2 flex gap-2">
                        <input type="date" value={form.startDate} onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))} className={withCreateFieldError('flex-1 px-3 py-2.5 rounded-xl bg-white border border-slate-200', 'startDate')} {...getCreateFieldProps('startDate')} />
                        <input type="time" value={form.startTime} onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))} className="w-28 px-3 py-2.5 rounded-xl bg-white border border-slate-200" />
                      </div>
                      {renderCreateFieldError('startDate')}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Campaign end<RequiredMark /></label>
                      <div className="mt-2 flex gap-2">
                        <input type="date" value={form.endDate} onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))} className={withCreateFieldError('flex-1 px-3 py-2.5 rounded-xl bg-white border border-slate-200', 'endDate')} {...getCreateFieldProps('endDate')} />
                        <input type="time" value={form.endTime} onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))} className="w-28 px-3 py-2.5 rounded-xl bg-white border border-slate-200" />
                      </div>
                      {renderCreateFieldError('endDate')}
                    </div>
                  </div>
                </div>
              )}

              {isBrandDealForm && (
                <div className="space-y-4 rounded-xl border border-sky-200 bg-sky-50/40 p-4">
                  <p className="text-xs font-bold text-sky-800 uppercase tracking-wider">
                    Brand campaign — budget subsidizes buyer; seller price & GST unchanged
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Brand<RequiredMark /></label>
                      <select
                        value={form.brandCampaignBrand}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            brandCampaignBrand: e.target.value,
                            brandCategoryFilter: '',
                            brandProductId: ''
                          }))
                        }
                        className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'brandCampaignBrand')}
                        {...getCreateFieldProps('brandCampaignBrand')}
                      >
                        <option value="">Select brand</option>
                        {brandOptions.map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                      {renderCreateFieldError('brandCampaignBrand')}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Category<RequiredMark /></label>
                      <select
                        value={form.brandCategoryFilter}
                        onChange={(e) => setForm((p) => ({ ...p, brandCategoryFilter: e.target.value, brandProductId: '' }))}
                        className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'brandCategoryFilter')}
                        disabled={!form.brandCampaignBrand}
                        {...getCreateFieldProps('brandCategoryFilter')}
                      >
                        <option value="">
                          {!form.brandCampaignBrand
                            ? 'Select brand first'
                            : brandCampaignCategoryOptions.length === 0
                              ? 'No categories for selected brand'
                              : 'Select category'}
                        </option>
                        {brandCampaignCategoryOptions.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      {renderCreateFieldError('brandCategoryFilter')}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Spare part <span className="font-normal normal-case text-slate-500">(optional)</span>
                      </label>
                      <select
                        value={form.brandProductId || ''}
                        onChange={(e) => setForm((p) => ({ ...p, brandProductId: e.target.value }))}
                        className="mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200"
                        disabled={!form.brandCampaignBrand || !form.brandCategoryFilter || productOptionsLoading}
                      >
                        <option value="">
                          {!form.brandCampaignBrand
                            ? 'Select brand first'
                            : !form.brandCategoryFilter
                              ? 'Select category first'
                              : productOptionsLoading
                                ? 'Loading spare parts…'
                                : brandCampaignProductOptions.length === 0
                                  ? 'No in-stock spare parts in this brand/category'
                                  : 'Optional: select spare part'}
                        </option>
                        {brandCampaignProductOptions.map((p) => {
                          const id = getProductId(p);
                          return (
                            <option key={id} value={id}>
                              {formatProductListingLabel(p)}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Campaign budget (₹)<RequiredMark /></label>
                      <input type="number" min="1" value={form.brandBudgetTotal} onChange={(e) => setForm((p) => ({ ...p, brandBudgetTotal: e.target.value }))} className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'brandBudgetTotal')} {...getCreateFieldProps('brandBudgetTotal')} />
                      {renderCreateFieldError('brandBudgetTotal')}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount type</label>
                      <div className="mt-2 flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                          <input
                            type="radio"
                            name="brandDiscountType"
                            checked={form.discountType === 'percent'}
                            onChange={() => setForm((p) => ({ ...p, discountType: 'percent' }))}
                          />
                          <span className="font-semibold">Percentage (%)</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                          <input
                            type="radio"
                            name="brandDiscountType"
                            checked={form.discountType === 'amount'}
                            onChange={() => setForm((p) => ({ ...p, discountType: 'amount' }))}
                          />
                          <span className="font-semibold">Fixed amount (₹)</span>
                        </label>
                      </div>
                    </div>
                    {form.discountType === 'percent' ? (
                      <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount (%)<RequiredMark /></label>
                        <input
                          type="number"
                          min="5"
                          max="70"
                          value={form.discountPct}
                          onChange={(e) => {
                            const nextPct = Math.max(5, Math.min(70, Number(e.target.value) || 0));
                            const base = getDiscountBaseForCurrentDealType();
                            const nextAmt = base > 0 ? Number(((base * nextPct) / 100).toFixed(2)) : 0;
                            setForm((p) => ({ ...p, discountPct: e.target.value, discountAmount: nextAmt }));
                          }}
                          className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'discountPct')}
                          {...getCreateFieldProps('discountPct')}
                        />
                        {renderCreateFieldError('discountPct')}
                        <p className="text-[11px] text-slate-600 mt-1">
                          Discount amount: <span className="font-semibold">{form.discountAmount ? formatCurrency(form.discountAmount) : '—'}</span> off
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount amount (₹)<RequiredMark /></label>
                        <input
                          type="number"
                          min="1"
                          value={form.discountAmount}
                          onChange={(e) => {
                            const nextAmt = Math.max(0, Number(e.target.value) || 0);
                            const base = getDiscountBaseForCurrentDealType();
                            const nextPct = base > 0 ? Number(((nextAmt / base) * 100).toFixed(2)) : 0;
                            setForm((p) => ({ ...p, discountAmount: e.target.value, discountPct: nextPct }));
                          }}
                          className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'discountPct')}
                          {...getCreateFieldProps('discountPct')}
                        />
                        {renderCreateFieldError('discountPct')}
                        <p className="text-[11px] text-slate-600 mt-1">
                          Discount (%): <span className="font-semibold">{Number(form.discountPct || 0).toFixed(2)}%</span>
                        </p>
                      </div>
                    )}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Status<RequiredMark /></label>
                      <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'status')} {...getCreateFieldProps('status')}>
                        <option value="Draft">Draft</option>
                        <option value="Scheduled">Scheduled</option>
                        <option value="Live">Live</option>
                      </select>
                      {renderCreateFieldError('status')}
                    </div>
                  </div>
                  {renderDealTimingSection()}
                  {brandPreviewLoading && (
                    <p className="text-[11px] text-slate-500">Checking products for this brand…</p>
                  )}
                  {!brandPreviewLoading && brandPreview && (
                    <p
                      className={`text-[11px] font-medium ${
                        brandPreview.canCreate === false ? 'text-red-700' : 'text-emerald-800'
                      }`}
                    >
                      {brandPreview.canCreate === false
                        ? brandPreview.message ||
                          'No eligible products for this brand. Add in-stock products with MRP and specifications.'
                        : `${brandPreview.inStock ?? 0} in-stock / ${brandPreview.total ?? 0} total matching product(s)${
                            brandPreview.anchorProduct?.name
                              ? ` — anchor: ${brandPreview.anchorProduct.name}`
                              : ''
                          }`}
                    </p>
                  )}
                  <p className="text-[11px] text-slate-600">8-minute reservation. Deal auto-pauses when budget is exhausted.</p>
                </div>
              )}

              {isComboDealForm && (
                <div className="space-y-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
                  <p className="text-xs font-bold text-indigo-900 uppercase tracking-wider">
                    Combo bundle — multiple products, single seller, discount on total price
                  </p>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Bundle name<RequiredMark /></label>
                    <input
                      value={form.comboBundleName}
                      onChange={(e) => setForm((p) => ({ ...p, comboBundleName: e.target.value }))}
                      className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'comboBundleName')}
                      {...getCreateFieldProps('comboBundleName')}
                    />
                    {renderCreateFieldError('comboBundleName')}
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Products in bundle<RequiredMark /></label>
                    <button
                      type="button"
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          comboItems: [
                            ...(Array.isArray(p.comboItems) ? p.comboItems : []),
                            { productId: '' }
                          ]
                        }))
                      }
                      disabled={productOptionsLoading}
                      className="px-3 py-1.5 rounded-lg border border-indigo-300 bg-white text-xs font-bold text-indigo-800 hover:bg-indigo-50 disabled:opacity-50"
                    >
                      + Add product
                    </button>
                  </div>
                  {productOptionsLoading && (
                    <p className="text-xs text-slate-500">Loading products for dropdown…</p>
                  )}
                  <div className="space-y-3">
                    {(Array.isArray(form.comboItems) ? form.comboItems : []).map((item, idx) => (
                      <div key={`combo-item-${idx}`} className="rounded-xl border border-white bg-white/80 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-bold text-slate-700">
                            Product {idx + 1}{idx === 0 ? ' (anchor)' : ''}<RequiredMark />
                          </p>
                          {(form.comboItems?.length || 0) > 2 && (
                            <button
                              type="button"
                              onClick={() =>
                                setForm((p) => {
                                  const arr = [...(Array.isArray(p.comboItems) ? p.comboItems : [])];
                                  arr.splice(idx, 1);
                                  return { ...p, comboItems: arr };
                                })
                              }
                              className="text-xs font-semibold text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <select
                          value={item.productId || ''}
                          onChange={(e) =>
                            setForm((p) => {
                              const arr = [...(Array.isArray(p.comboItems) ? p.comboItems : [])];
                              arr[idx] = { productId: e.target.value };
                              return { ...p, comboItems: arr };
                            })
                          }
                          className={withCreateFieldError('w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white', `comboItem-${idx}`)}
                          disabled={productOptionsLoading}
                          {...getCreateFieldProps(`comboItem-${idx}`)}
                        >
                          <option value="">
                            {productOptionsLoading ? 'Loading products…' : 'Select spare part'}
                          </option>
                          {getComboSelectableProducts(idx).map((p) => {
                            const id = getProductId(p);
                            return (
                              <option key={id} value={id}>
                                {formatProductListingLabel(p)}
                              </option>
                            );
                          })}
                        </select>
                        {renderCreateFieldError(`comboItem-${idx}`)}
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500">
                    All selected products must be from the same seller. Bundle price updates automatically below.
                  </p>
                  <div className="space-y-2">
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount type</label>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                        <input
                          type="radio"
                          name="comboDiscountType"
                          checked={form.discountType === 'percent'}
                          onChange={() => setForm((p) => ({ ...p, discountType: 'percent' }))}
                        />
                        <span className="font-semibold">Percentage (%)</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                        <input
                          type="radio"
                          name="comboDiscountType"
                          checked={form.discountType === 'amount'}
                          onChange={() => setForm((p) => ({ ...p, discountType: 'amount' }))}
                        />
                        <span className="font-semibold">Fixed amount (₹)</span>
                      </label>
                    </div>
                    {form.discountType === 'percent' ? (
                      <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount (%)<RequiredMark /></label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={form.comboDiscountPct}
                          onChange={(e) => {
                            const nextPct = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                            const base = getDiscountBaseForCurrentDealType();
                            const nextAmt = base > 0 ? Number(((base * nextPct) / 100).toFixed(2)) : 0;
                            setForm((p) => ({ ...p, comboDiscountPct: e.target.value, discountAmount: nextAmt }));
                          }}
                          className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'comboDiscountPct')}
                          {...getCreateFieldProps('comboDiscountPct')}
                        />
                        {renderCreateFieldError('comboDiscountPct')}
                        <p className="text-[11px] text-slate-600 mt-1">
                          Discount amount: <span className="font-semibold">{form.discountAmount ? formatCurrency(form.discountAmount) : '—'}</span> off
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount amount (₹)<RequiredMark /></label>
                        <input
                          type="number"
                          min="1"
                          value={form.discountAmount}
                          onChange={(e) => {
                            const nextAmt = Math.max(0, Number(e.target.value) || 0);
                            const base = getDiscountBaseForCurrentDealType();
                            const nextPct = base > 0 ? Number(((nextAmt / base) * 100).toFixed(2)) : 0;
                            setForm((p) => ({ ...p, discountAmount: e.target.value, comboDiscountPct: nextPct }));
                          }}
                          className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'comboDiscountPct')}
                          {...getCreateFieldProps('comboDiscountPct')}
                        />
                        {renderCreateFieldError('comboDiscountPct')}
                        <p className="text-[11px] text-slate-600 mt-1">
                          Discount (%): <span className="font-semibold">{Number(form.comboDiscountPct || 0).toFixed(2)}%</span>
                        </p>
                      </div>
                    )}
                    <p className="text-[11px] text-slate-500 mt-1">
                      Total = sum of each product price. Bundle price = total − discount%. Pool capped at weakest stock.
                    </p>
                    {Number(form.comboDiscountPct) > 40 && (
                      <p className="text-[11px] text-amber-700 mt-1">{getDiscountApprovalHint()}</p>
                    )}
                  </div>
                  {comboPreviewReady && comboPreviewLoading && (
                    <p className="text-xs font-semibold text-indigo-700">Calculating bundle price…</p>
                  )}
                  {comboPreviewReady && comboQualificationReport && (
                    <div className={`rounded-xl border p-3 text-xs ${comboQualificationReport.qualifyingSellerCount > 0 ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                      <p className="font-bold">
                        {comboQualificationReport.qualifyingSellerCount > 0
                          ? `${comboQualificationReport.qualifyingSellerCount} seller(s) qualify`
                          : '0 sellers qualify'}
                      </p>
                      {(Array.isArray(comboQualificationReport.items) ? comboQualificationReport.items : []).map((row) => (
                        <p key={`${row.label}-${row.productId || row.index}`} className="mt-1">
                          {row.name
                            ? `${row.label}: ${row.name} — ${formatCurrency(row.price)}`
                            : `${row.label}: ${row.matchingProductCount ?? 0} products / ${row.sellerCount ?? 0} sellers`}
                        </p>
                      ))}
                      {comboQualificationReport.pricingPreview && (
                        <p className="mt-2 font-semibold">
                          Total {formatCurrency(comboQualificationReport.pricingPreview.totalCatalogPrice)}
                          {' → '}
                          {Number(form.comboDiscountPct) || 0}% off = {formatCurrency(comboQualificationReport.pricingPreview.bundlePrice)}
                        </p>
                      )}
                      {comboQualificationReport.qualifyingSellerCount === 0 && comboQualificationReport.suggestedFix && (
                        <p className="mt-2 font-semibold">Suggested fix: {comboQualificationReport.suggestedFix}</p>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Status<RequiredMark /></label>
                    <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'status')} {...getCreateFieldProps('status')}>
                      {STANDARD_DEAL_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {renderCreateFieldError('status')}
                    {form.status === 'Draft' && (
                      <p className="text-[11px] text-slate-500 mt-1">Draft deals are not shown on the website.</p>
                    )}
                  </div>
                  {renderDealTimingSection()}
                </div>
              )}

              {isCouponDealForm && (
                <div className="space-y-4 rounded-xl border border-violet-200 bg-violet-50/40 p-4">
                  <p className="text-xs font-bold text-violet-900 uppercase tracking-wider">
                    Coupon campaign — 6 fields (admin only)
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Coupon code<RequiredMark /></label>
                      <input value={form.couponCode} onChange={(e) => setForm((p) => ({ ...p, couponCode: e.target.value.toUpperCase() }))} className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'couponCode')} {...getCreateFieldProps('couponCode')} />
                      {renderCreateFieldError('couponCode')}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount type</label>
                      <div className="mt-2 flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                          <input
                            type="radio"
                            name="couponDiscountType"
                            checked={form.couponDiscountType === 'percent'}
                            onChange={() => setForm((p) => ({ ...p, couponDiscountType: 'percent' }))}
                          />
                          <span className="font-semibold">Percentage (%)</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                          <input
                            type="radio"
                            name="couponDiscountType"
                            checked={form.couponDiscountType === 'amount'}
                            onChange={() => setForm((p) => ({ ...p, couponDiscountType: 'amount' }))}
                          />
                          <span className="font-semibold">Fixed amount (₹)</span>
                        </label>
                      </div>
                    </div>
                    {form.couponDiscountType === 'percent' ? (
                      <>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount (%)<RequiredMark /></label>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={form.discountPct}
                            onChange={(e) => setForm((p) => ({ ...p, discountPct: e.target.value }))}
                            className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'discountPct')}
                            {...getCreateFieldProps('discountPct')}
                          />
                          {renderCreateFieldError('discountPct')}
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Max discount cap (₹, optional)</label>
                          <input
                            type="number"
                            min="0"
                            value={form.couponMaxDiscountAmount}
                            onChange={(e) => setForm((p) => ({ ...p, couponMaxDiscountAmount: e.target.value }))}
                            className="mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200"
                            placeholder="No cap"
                          />
                          <p className="text-[11px] text-slate-500 mt-1">Caps the % discount on large orders. Leave 0 for no cap.</p>
                          <p className="text-[11px] text-slate-600 mt-1">
                            Approx fixed discount at min order: <span className="font-semibold">
                              {(() => {
                                const minOrder = Math.max(0, Number(form.couponMinOrderValue) || 0);
                                const pct = Math.max(0, Number(form.discountPct) || 0);
                                const cap = Math.max(0, Number(form.couponMaxDiscountAmount) || 0);
                                if (!minOrder || !pct) return '—';
                                const raw = (minOrder * pct) / 100;
                                const amt = cap > 0 ? Math.min(raw, cap) : raw;
                                return formatCurrency(Number(amt.toFixed(2)));
                              })()}
                            </span> off
                          </p>
                        </div>
                      </>
                    ) : (
                      <div className="md:col-span-2">
                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount amount (₹)<RequiredMark /></label>
                        <input
                          type="number"
                          min="1"
                          value={form.couponDiscountAmount}
                          onChange={(e) => setForm((p) => ({ ...p, couponDiscountAmount: e.target.value }))}
                          className={withCreateFieldError('mt-2 w-full max-w-md px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'couponDiscountAmount')}
                          {...getCreateFieldProps('couponDiscountAmount')}
                        />
                        {renderCreateFieldError('couponDiscountAmount')}
                        <p className="text-[11px] text-slate-500 mt-1">Flat ₹ off the cart subtotal (cannot exceed subtotal at checkout).</p>
                        <p className="text-[11px] text-slate-600 mt-1">
                          Approx percentage at min order: <span className="font-semibold">
                            {(() => {
                              const minOrder = Math.max(0, Number(form.couponMinOrderValue) || 0);
                              const amt = Math.max(0, Number(form.couponDiscountAmount) || 0);
                              if (!minOrder || !amt) return '—';
                              const pct = (amt / minOrder) * 100;
                              return `${Number(pct.toFixed(2))}%`;
                            })()}
                          </span>
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Min order value (₹)<RequiredMark /></label>
                      <input type="number" min="0" value={form.couponMinOrderValue} onChange={(e) => setForm((p) => ({ ...p, couponMinOrderValue: e.target.value }))} className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'couponMinOrderValue')} {...getCreateFieldProps('couponMinOrderValue')} />
                      {renderCreateFieldError('couponMinOrderValue')}
                    </div>
                    <div
                      data-create-field="couponScopeType"
                      className={`md:col-span-2 rounded-xl p-3 ${isCreateFieldInvalid('couponScopeType') || isCreateFieldInvalid('couponScopeLabel') ? 'deal-field-invalid-group' : ''}`}
                    >
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Applicable scope<RequiredMark /></label>
                      <p className="text-[11px] text-slate-500 mt-1 mb-2">
                        Choose whether this coupon works on every product or only items in one category.
                      </p>
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                          <input
                            type="radio"
                            name="couponScopeType"
                            checked={form.couponScopeType === 'all'}
                            onChange={() => setForm((p) => ({ ...p, couponScopeType: 'all', couponScopeLabel: '' }))}
                          />
                          <span className="font-semibold">All products</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                          <input
                            type="radio"
                            name="couponScopeType"
                            checked={form.couponScopeType === 'category'}
                            onChange={() => setForm((p) => ({ ...p, couponScopeType: 'category' }))}
                          />
                          <span className="font-semibold">Specific category</span>
                        </label>
                      </div>
                      {form.couponScopeType === 'category' && (
                        <div className="mt-3">
                          <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider">Product category<RequiredMark /></label>
                          <select
                            value={form.couponScopeLabel}
                            onChange={(e) => setForm((p) => ({ ...p, couponScopeLabel: e.target.value }))}
                            className={withCreateFieldError('mt-2 w-full max-w-md px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-sm', 'couponScopeLabel')}
                            disabled={catalogCategoriesLoading || couponScopeCategoryOptions.length === 0}
                            {...getCreateFieldProps('couponScopeLabel')}
                          >
                            <option value="">
                              {catalogCategoriesLoading ? 'Loading categories…' : 'Select category'}
                            </option>
                            {couponScopeCategoryOptions.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                          {renderCreateFieldError('couponScopeLabel')}
                          {!catalogCategoriesLoading && couponScopeCategoryOptions.length === 0 && (
                            <p className="text-[11px] text-amber-700 mt-1">No categories found. Add categories in Admin → Categories.</p>
                          )}
                          {form.couponScopeLabel && (
                            <p className="text-[11px] text-indigo-800 mt-1 font-medium">
                              Coupon valid only for products in: {form.couponScopeLabel}
                            </p>
                          )}
                        </div>
                      )}
                      {form.couponScopeType === 'all' && (
                        <p className="text-[11px] text-emerald-800 mt-2 font-medium">Coupon can be used on any product in the cart.</p>
                      )}
                      {renderCreateFieldError('couponScopeType')}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Per-user limit<RequiredMark /></label>
                      <input type="number" min="1" value={form.couponUsageLimitPerUser} onChange={(e) => setForm((p) => ({ ...p, couponUsageLimitPerUser: e.target.value }))} className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'couponUsageLimitPerUser')} {...getCreateFieldProps('couponUsageLimitPerUser')} />
                      {renderCreateFieldError('couponUsageLimitPerUser')}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={!!form.couponStackableWithDeals}
                      onChange={(e) => setForm((p) => ({ ...p, couponStackableWithDeals: e.target.checked }))}
                    />
                    <span>Allow stacking with other deals (off by default)</span>
                  </label>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Status<RequiredMark /></label>
                    <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'status')} {...getCreateFieldProps('status')}>
                      {STANDARD_DEAL_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {renderCreateFieldError('status')}
                    {form.status === 'Draft' && (
                      <p className="text-[11px] text-slate-500 mt-1">Draft coupons are not shown on the website.</p>
                    )}
                  </div>
                  {renderDealTimingSection()}
                  <p className="text-[11px] text-slate-600">Checkout shows a specific message for each failed condition (min order, scope, limits, dates).</p>
                </div>
              )}

              {isGarageServiceDealForm && (
                <div className="space-y-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
                  <p className="text-xs font-bold text-indigo-900 uppercase tracking-wider">
                    {isAdminGarageCampaignForm
                      ? 'Platform garage campaign — garages in the selected city can join'
                      : 'Garage service deal — applies to services from your registration'}
                  </p>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Deal title<RequiredMark /></label>
                    <input
                      value={form.title}
                      onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                      className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'title')}
                      placeholder={isAdminGarageCampaignForm ? 'e.g., Gurugram AC Service Fest' : 'e.g., AC Service Summer Offer'}
                      {...getCreateFieldProps('title')}
                    />
                    {renderCreateFieldError('title')}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Service<RequiredMark /></label>
                      <select
                        value={form.garageServiceId}
                        onChange={(e) => setForm((p) => ({ ...p, garageServiceId: e.target.value }))}
                        className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'garageServiceId')}
                        disabled={!isAdminGarageCampaignForm && garageServiceOptions.length === 0}
                        {...getCreateFieldProps('garageServiceId')}
                      >
                        <option value="">
                          {isAdminGarageCampaignForm
                            ? 'Select service'
                            : garageServiceOptions.length === 0
                              ? 'Complete cluster registration first'
                              : 'Select service'}
                        </option>
                        {groupServiceLinesByCluster(
                          isAdminGarageCampaignForm ? getFullCatalogServiceLines('large') : garageServiceOptions
                        ).map((group) => (
                          <optgroup key={group.label} label={group.label}>
                            {group.items.map((line) => (
                              <option
                                key={line.serviceId}
                                value={line.serviceId}
                                disabled={
                                  !isAdminGarageCampaignForm &&
                                  isPackageDealBlockedForTier(garageProfileTier, line.serviceId)
                                }
                              >
                                {line.label}
                                {line.packageEligible ? ' (Package)' : ''}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      {renderCreateFieldError('garageServiceId')}
                      <p className="text-[11px] text-slate-500 mt-1">
                        {isAdminGarageCampaignForm
                          ? 'Structured catalog services for city-wide campaigns.'
                          : garageServiceOptions.length === 0
                            ? 'Register service clusters during partner signup to unlock deal services.'
                            : 'Services auto-expanded from your clusters. Package deals need Medium or Large tier.'}
                      </p>
                    </div>
                    {isAdminGarageCampaignForm && (
                      <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
                          Cities<RequiredMark />
                        </label>
                        <GarageCitiesMultiDropdown
                          cities={registeredGarageCities}
                          value={form.garageServiceCities}
                          loading={registeredGarageCitiesLoading}
                          disabled={registeredGarageCities.length === 0}
                          invalid={isCreateFieldInvalid('garageServiceCities')}
                          onChange={(next) => setForm((p) => ({ ...p, garageServiceCities: next }))}
                        />
                        {renderCreateFieldError('garageServiceCities')}
                        <p className="text-[11px] text-slate-500 mt-1">
                          {Array.isArray(form.garageServiceCities) && form.garageServiceCities.length > 0 && (
                            <span className="block mt-0.5 font-medium text-indigo-700">
                              Selected ({form.garageServiceCities.length}): {form.garageServiceCities.join(', ')}
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                  {isAdminGarageCampaignForm && (
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Pincodes (optional)
                      </label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={GARAGE_PINCODE_LENGTH}
                          value={form.garagePincodeDraft || ''}
                          onChange={(e) => {
                            setGaragePincodeError('');
                            setForm((p) => ({
                              ...p,
                              garagePincodeDraft: normalizeGaragePincodeInput(e.target.value)
                            }));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addGaragePincode();
                            }
                          }}
                          className={`flex-1 min-w-[140px] px-4 py-2.5 rounded-xl bg-white border ${
                            garagePincodeError ? 'border-rose-300' : 'border-slate-200'
                          }`}
                          placeholder="110001"
                        />
                        <button
                          type="button"
                          onClick={addGaragePincode}
                          className="px-4 py-2.5 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-800 text-sm font-semibold hover:bg-indigo-100"
                        >
                          Add pincode
                        </button>
                      </div>
                      {garagePincodeError && (
                        <p className="text-[11px] text-rose-600 font-semibold mt-1">{garagePincodeError}</p>
                      )}
                      {Array.isArray(form.garageServiceAreaPincodes) && form.garageServiceAreaPincodes.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {form.garageServiceAreaPincodes.map((pin) => (
                            <span
                              key={pin}
                              className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-900"
                            >
                              {pin}
                              <button
                                type="button"
                                onClick={() => removeGaragePincode(pin)}
                                className="text-indigo-600 hover:text-rose-600 font-bold leading-none"
                                aria-label={`Remove pincode ${pin}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-[11px] text-slate-500 mt-1">
                        Each pincode must be exactly 6 digits. Leave empty for all garages in the selected cities. If you add pincodes, only garages with a matching profile pincode get notified and can participate.
                      </p>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount type</label>
                    <div className="mt-2 flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                        <input
                          type="radio"
                          name="garageDiscountType"
                          checked={form.garageDiscountType === 'percent'}
                          onChange={() => setForm((p) => ({ ...p, garageDiscountType: 'percent' }))}
                        />
                        <span className="font-semibold">Percentage (%)</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                        <input
                          type="radio"
                          name="garageDiscountType"
                          checked={form.garageDiscountType === 'amount'}
                          onChange={() => setForm((p) => ({ ...p, garageDiscountType: 'amount' }))}
                        />
                        <span className="font-semibold">Fixed amount (₹)</span>
                      </label>
                    </div>
                  </div>
                  {form.garageDiscountType === 'percent' ? (
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount (%)<RequiredMark /></label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={form.discountPct}
                        onChange={(e) => setForm((p) => ({ ...p, discountPct: e.target.value }))}
                        className={withCreateFieldError('mt-2 w-full max-w-xs px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'discountPct')}
                        {...getCreateFieldProps('discountPct')}
                      />
                      {renderCreateFieldError('discountPct')}
                      {Number(form.discountPct) > 40 && (
                        <p className="text-[11px] text-amber-700 mt-1">{getDiscountApprovalHint()}</p>
                      )}
                      <p className="text-[11px] text-slate-600 mt-1">
                        Approx fixed amount (₹): <span className="font-semibold">
                          {(() => {
                            const pct = Math.max(0, Number(form.discountPct) || 0);
                            if (!pct) return '—';
                            const amt = (GARAGE_APPROVAL_LOCKED_BASE * pct) / 100;
                            return formatCurrency(Number(amt.toFixed(2)));
                          })()}
                        </span> off
                      </p>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount amount (₹)<RequiredMark /></label>
                      <input
                        type="number"
                        min="1"
                        value={form.garageDiscountAmount}
                        onChange={(e) => setForm((p) => ({ ...p, garageDiscountAmount: e.target.value }))}
                        className={withCreateFieldError('mt-2 w-full max-w-xs px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'garageDiscountAmount')}
                        {...getCreateFieldProps('garageDiscountAmount')}
                      />
                      {renderCreateFieldError('garageDiscountAmount')}
                      <p className="text-[11px] text-slate-600 mt-1">
                        Approx percentage: <span className="font-semibold">
                          {(() => {
                            const amt = Math.max(0, Number(form.garageDiscountAmount) || 0);
                            if (!amt) return '—';
                            const pct = (amt / GARAGE_APPROVAL_LOCKED_BASE) * 100;
                            return `${Number(pct.toFixed(2))}%`;
                          })()}
                        </span>
                      </p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Status<RequiredMark /></label>
                    <select
                      value={form.status}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (next === 'Active' && garageFormNeedsApproval) {
                          toast.warning(GARAGE_ACTIVE_REQUIRES_ADMIN_MSG);
                          setForm((p) => ({ ...p, status: 'Draft' }));
                          return;
                        }
                        setForm((p) => ({ ...p, status: next }));
                      }}
                      className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200', 'status')}
                      {...getCreateFieldProps('status')}
                    >
                      {STANDARD_DEAL_STATUS_OPTIONS.map((opt) => (
                        <option
                          key={opt.value}
                          value={opt.value}
                          disabled={opt.value === 'Active' && garageFormNeedsApproval}
                        >
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {renderCreateFieldError('status')}
                    {garageFormNeedsApproval && (
                      <p className="text-[11px] text-amber-700 mt-1 font-medium">
                        Discount above {GARAGE_APPROVAL_DISCOUNT_THRESHOLD}% is saved as <strong>Draft</strong> and sent for admin approval. When admin approves, the deal becomes <strong>Active</strong> automatically — you do not need to change status yourself.
                      </p>
                    )}
                  </div>
                  {renderDealTimingSection('slate')}
                  <p className="text-[11px] text-slate-600">
                    {isAdminGarageCampaignForm
                      ? 'After you publish, matching garages in the selected cities (and pincodes, if set) are notified and can click Participate.'
                      : garageFormNeedsApproval
                        ? 'After admin approves, the deal turns Active automatically and can show on home garage cards.'
                        : (
                          <>
                            Set status to <strong>Active</strong> for the discount badge on home garage cards (Draft stays hidden).
                            If you create multiple deals, the highest discount is shown.
                          </>
                        )}
                  </p>
                </div>
              )}

              {isVendorProductDealForm && (
              <>
              {isClearanceDealForm && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2.5 space-y-1">
                  <p className="text-[11px] text-amber-900 font-medium">
                    Slow-moving part only (no sale in {clearanceRules.triggerDays ?? 45}d, sell rate ≤{' '}
                    {clearanceRules.sellRateThresholdPct ?? 10}% in {clearanceRules.windowDays ?? 90}d).
                  </p>
                  <p className="text-[11px] font-semibold text-red-800">Final sale — no returns (locked at creation).</p>
                </div>
              )}
              {isPlatformStaffRole && isSellerOwnDealForm && (
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
                    Seller ID<RequiredMark />
                  </label>
                  <select
                    value={form.sellerOwnSellerId || ''}
                    onChange={(e) => {
                      const nextId = e.target.value;
                      setForm((p) => ({
                        ...p,
                        sellerOwnSellerId: nextId,
                        productPickerBrand: '',
                        productPickerCategory: ''
                      }));
                      setSelectedProduct(null);
                      setProductError('');
                      resetSellerOwnOtpFlow();
                    }}
                    className={withCreateFieldError(
                      'mt-2 w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-200',
                      'sellerOwnSellerId'
                    )}
                    disabled={sellerOptionsLoading}
                    {...getCreateFieldProps('sellerOwnSellerId')}
                  >
                    <option value="">
                      {sellerOptionsLoading ? 'Loading sellers…' : 'Select seller (user ID)'}
                    </option>
                    {sellerOptions.map((seller) => (
                      <option key={seller.id} value={seller.id}>
                        {seller.name} — {seller.email || seller.role} — ID: {seller.id}
                      </option>
                    ))}
                  </select>
                  {renderCreateFieldError('sellerOwnSellerId')}
                  <p className="text-[11px] text-slate-500 mt-1">
                    Choose the seller user. Spare parts below are filtered to that seller&apos;s listings only.
                    {sellerOwnOtpStep === 'idle' && (
                      <span className="block mt-0.5 text-indigo-700 font-medium">
                        On save, an OTP is sent to the seller&apos;s registered mobile. Enter it to verify and create the deal.
                      </span>
                    )}
                  </p>
                </div>
              )}
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Spare part{!pendingClearanceDraftId && <RequiredMark />}
                </label>

                    {dealCategoryRequiresStructuredAttributes(form.category) &&
                      selectedProduct &&
                      getDealStructuredAttributeGaps(selectedProduct).length > 0 && (
                        <p className="mt-2 text-xs font-semibold text-red-600">
                          {formatDealEligibilityError(selectedProduct, form.category)}
                        </p>
                      )}

                    {selectedProduct ? (
                  <div
                    data-create-field="product"
                    tabIndex={isCreateFieldInvalid('product') ? -1 : undefined}
                    className={`mt-2 rounded-xl border px-4 py-3 ${
                    isCreateFieldInvalid('product')
                      ? 'deal-field-invalid bg-red-50/60'
                      : 'border-emerald-200 bg-emerald-50'
                  }`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-bold text-emerald-900 truncate min-w-0">
                        {isClearanceDealForm
                          ? formatClearanceProductLabel(selectedProduct)
                          : formatProductListingLabel(selectedProduct)}
                      </p>
                      {!pendingClearanceDraftId && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedProduct(null);
                            setForm((p) => ({ ...p, productPickerBrand: '', productPickerCategory: '' }));
                          }}
                          className="shrink-0 px-3 py-1.5 rounded-lg bg-white border border-emerald-200 text-emerald-800 text-xs font-bold hover:bg-emerald-100"
                        >
                          Change
                        </button>
                      )}
                    </div>
                    {isClearanceDealForm && renderClearanceMetricsSummary(selectedProduct)}
                    {renderCreateFieldError('product')}
                  </div>
                ) : (
                  <div className="mt-2">
                    <select
                      value=""
                      onChange={(e) => {
                        const id = e.target.value;
                        if (!id) return;
                        const chosen = productOptions.find((p) => String(getProductId(p)) === String(id));
                        if (chosen) {
                          selectProductWithStockCheck(chosen);
                        }
                      }}
                      className={withCreateFieldError('w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-200', 'product')}
                      {...getCreateFieldProps('product')}
                      disabled={
                        productOptionsLoading ||
                        productLoading ||
                        !!pendingClearanceDraftId ||
                        (isPlatformStaffRole && isSellerOwnDealForm && !form.sellerOwnSellerId)
                      }
                    >
                      <option value="">
                        {productOptionsLoading
                          ? 'Loading spare parts...'
                          : isPlatformStaffRole && isSellerOwnDealForm && !form.sellerOwnSellerId
                            ? 'Select seller ID first'
                          : isClearanceDealForm
                            ? 'Select slow-moving spare part'
                            : 'Select spare part'}
                      </option>
                      {productOptions.map((p) => {
                        const id = getProductId(p);
                        if (!id) return null;
                        const label = isClearanceDealForm
                          ? formatClearanceProductLabel(p)
                          : formatProductListingLabel(p);
                        return (
                          <option key={id} value={id}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                    {renderCreateFieldError('product')}

                    {productError && createFieldErrors.length === 0 && (
                      <p className="mt-2 text-xs font-semibold text-red-600">{productError}</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Title<RequiredMark />
                </label>
                <input
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-200', 'title')}
                  placeholder={
                    isClearanceDealForm
                      ? 'Auto-filled from product; edit if needed'
                      : 'e.g., Castrol EDGE 5W-30 4L'
                  }
                  {...getCreateFieldProps('title')}
                />
                {renderCreateFieldError('title')}
              </div>

              <div className="space-y-3">
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Discount type</label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                    <input
                      type="radio"
                      name="vendorProductDiscountType"
                      checked={form.discountType === 'percent'}
                      onChange={() => setForm((p) => ({ ...p, discountType: 'percent' }))}
                    />
                    <span className="font-semibold">Percentage (%)</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
                    <input
                      type="radio"
                      name="vendorProductDiscountType"
                      checked={form.discountType === 'amount'}
                      onChange={() => setForm((p) => ({ ...p, discountType: 'amount' }))}
                    />
                    <span className="font-semibold">Fixed amount (₹)</span>
                  </label>
                </div>
                {form.discountType === 'percent' ? (
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
                      Discount (%)<RequiredMark />
                    </label>
                    <input
                      type="number"
                      min={isClearanceDealForm ? clearanceMinDiscount : 0}
                      max={isClearanceDealForm ? clearanceMaxDiscount : 100}
                      value={form.discountPct}
                      onChange={(e) => {
                        const nextPct = e.target.value;
                        const numericPct = Math.max(0, Number(nextPct) || 0);
                        const base = getDiscountBaseForCurrentDealType();
                        const nextAmt = base > 0 ? Number(((base * numericPct) / 100).toFixed(2)) : 0;
                        setForm((p) => ({ ...p, discountPct: nextPct, discountAmount: nextAmt }));
                      }}
                      className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-200', 'discountPct')}
                      {...getCreateFieldProps('discountPct')}
                    />
                    {renderCreateFieldError('discountPct')}
                    <p className="text-[11px] text-slate-600 mt-1">
                      Discount amount: <span className="font-semibold">{form.discountAmount ? formatCurrency(form.discountAmount) : '—'}</span> off
                    </p>
                    {isClearanceDealForm && (
                      <p className="text-[11px] text-slate-500 mt-1">
                        Clearance range: {clearanceMinDiscount}–{clearanceMaxDiscount}%
                      </p>
                    )}
                    {Number(form.discountPct) > 40 && (
                      <p className="text-[11px] text-amber-700 mt-1">{getDiscountApprovalHint()}</p>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
                      Discount amount (₹)<RequiredMark />
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={form.discountAmount ?? 0}
                      onChange={(e) => {
                        const nextAmt = Math.max(0, Number(e.target.value) || 0);
                        const base = getDiscountBaseForCurrentDealType();
                        let nextPct = 0;
                        if (base > 0) {
                          const pctFromAmt = Number(((nextAmt / base) * 100).toFixed(2));
                          if (isClearanceDealForm) nextPct = Math.max(clearanceMinDiscount, Math.min(clearanceMaxDiscount, pctFromAmt));
                          else nextPct = Math.max(0, Math.min(100, pctFromAmt));
                        }
                        setForm((p) => ({ ...p, discountAmount: e.target.value, discountPct: nextPct }));
                      }}
                      className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-200', 'discountPct')}
                      {...getCreateFieldProps('discountPct')}
                    />
                    {renderCreateFieldError('discountPct')}
                    <p className="text-[11px] text-slate-600 mt-1">
                      Discount (%): <span className="font-semibold">{Number(form.discountPct || 0).toFixed(2)}%</span>
                    </p>
                  </div>
                )}
              </div>

              {!isPlatformStaffRole && isSellerOwnDealForm && (
                <label className="flex items-start gap-2 cursor-pointer rounded-xl border border-violet-100 bg-violet-50/80 px-3 py-2.5">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!form.requestHomepagePlacement}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, requestHomepagePlacement: e.target.checked }))
                    }
                  />
                  <span className="text-xs text-violet-950">
                    <strong>Request homepage placement</strong> — admin will approve and set payment
                    terms before the deal can appear on the homepage.
                  </span>
                </label>
              )}

              {showPoolCommitment && (
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
                    Deal Pool Commitment (%)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={form.commitmentPercentage}
                    onChange={(e) => setForm((p) => ({ ...p, commitmentPercentage: e.target.value }))}
                    disabled={!isPlatformStaffRole}
                    className="mt-2 w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:opacity-70"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Percentage of current spare-part stock dedicated to this deal pool.
                    {!isPlatformStaffRole ? ' Admin controls this value.' : ''}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">Status<RequiredMark /></label>
                <select
                  value={form.status}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                  className={withCreateFieldError('mt-2 w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-200', 'status')}
                  {...getCreateFieldProps('status')}
                >
                  {STANDARD_DEAL_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {renderCreateFieldError('status')}
                {form.status === 'Draft' && (
                  <p className="text-[11px] text-slate-500 mt-1">Draft deals are not shown on the website.</p>
                )}
              </div>
              {renderDealTimingSection('slate')}

              </>
              )}

              {canManageParticipationRoles && !isComboDealForm && !isCouponDealForm && !isGarageServiceDealForm && !isVendorProductDealForm && (
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Who can participate (roles)
                </label>
                <p className="text-[11px] text-slate-500 mt-1">
                  Empty = everyone. Selected roles only see and use this deal (push sent to those roles).
                </p>
                <div className="mt-2 relative">
                  <button
                    type="button"
                    onClick={() => setRolesDropdownOpen((v) => !v)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 transition"
                  >
                    <div className="min-w-0 text-left">
                      <p className="text-sm font-semibold text-slate-900">
                        {rolesAudience === 'all'
                          ? 'Everyone'
                          : (Array.isArray(form.eligibleRoles) && form.eligibleRoles.length > 0
                            ? `${form.eligibleRoles.length} role(s) selected`
                            : 'Select roles')}
                      </p>
                      <p className="text-[11px] text-slate-500 truncate">
                        {rolesAudience === 'all'
                          ? 'All roles can participate'
                          : 'Only selected roles will receive notification'}
                      </p>
                    </div>
                    <span className="text-slate-500 text-sm">{rolesDropdownOpen ? '▲' : '▼'}</span>
                  </button>

                  {rolesDropdownOpen && (
                    <div className="absolute z-50 mt-2 w-full rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                      <div className="p-4 border-b border-slate-100">
                        <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Audience</p>
                        <div className="mt-3 space-y-2">
                          <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                              type="radio"
                              name="dealAudience"
                              checked={rolesAudience === 'all'}
                              onChange={() => {
                                setRolesAudience('all');
                                setForm((p) => ({ ...p, eligibleRoles: [] })); // empty => open for all
                              }}
                            />
                            <span className="font-semibold">Everyone</span>
                          </label>
                          <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                              type="radio"
                              name="dealAudience"
                              checked={rolesAudience === 'selected'}
                              onChange={() => setRolesAudience('selected')}
                            />
                            <span className="font-semibold">Specific roles</span>
                          </label>
                        </div>
                      </div>

                      {rolesAudience === 'selected' && (
                        <div className="p-4">
                          {rolesLoading ? (
                            <p className="text-xs text-slate-500">Loading roles…</p>
                          ) : rolesOptions.length === 0 ? (
                            <p className="text-xs text-slate-500">No roles found.</p>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {rolesOptions
                                .filter((r) => r.id !== 'superadmin' && r.id !== 'pending' && r.id !== 'used_pending' && r.id !== 'shipping')
                                .map((r) => {
                                  const checked = Array.isArray(form.eligibleRoles) && form.eligibleRoles.includes(r.id);
                                  return (
                                    <label key={r.id} className="flex items-center gap-2 text-sm text-slate-700">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                          const on = e.target.checked;
                                          setForm((p) => {
                                            const prev = Array.isArray(p.eligibleRoles) ? p.eligibleRoles : [];
                                            const next = on ? Array.from(new Set([...prev, r.id])) : prev.filter((x) => x !== r.id);
                                            return { ...p, eligibleRoles: next };
                                          });
                                        }}
                                      />
                                      <span className="text-xs font-semibold">{r.label || r.id}</span>
                                    </label>
                                  );
                                })}
                            </div>
                          )}

                          <div className="mt-3 flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setForm((p) => ({ ...p, eligibleRoles: [] }));
                              }}
                              className="px-3 py-2 rounded-xl border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50"
                            >
                              Clear
                            </button>
                            <button
                              type="button"
                              onClick={() => setRolesDropdownOpen(false)}
                              className="px-3 py-2 rounded-xl bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700"
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      )}

                      {rolesAudience === 'all' && (
                        <div className="p-4">
                          <p className="text-xs text-slate-600">
                            Everyone will get notified about this deal and can participate.
                          </p>
                          <div className="mt-3 flex items-center justify-end">
                            <button
                              type="button"
                              onClick={() => setRolesDropdownOpen(false)}
                              className="px-3 py-2 rounded-xl bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700"
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              )}

              {productError && createFieldErrors.length === 0 && (
                <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                  {productError}
                </div>
              )}

              {isPlatformStaffRole && isSellerOwnDealForm && sellerOwnOtpStep === 'sent' && (
                <div
                  data-create-field="sellerOwnOtp"
                  className={`rounded-xl p-4 space-y-3 ${
                    isCreateFieldInvalid('sellerOwnOtp')
                      ? 'deal-field-invalid-group bg-red-50/40'
                      : 'border border-indigo-200 bg-indigo-50/60'
                  }`}
                >
                  <div>
                    <p className="text-xs font-bold text-indigo-900 uppercase tracking-wider">
                      Seller OTP verification<RequiredMark />
                    </p>
                    <p className="text-[11px] text-indigo-800 mt-1">
                      OTP sent to seller mobile{sellerOwnOtpHint ? ` ${sellerOwnOtpHint}` : ''}. Enter the code to create the deal.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 items-end">
                    <div className="flex-1 min-w-[140px]">
                      <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                        Enter OTP
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={sellerOwnOtp}
                        onChange={(e) => {
                          setSellerOwnOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                          setProductError('');
                        }}
                        className={withCreateFieldError(
                          'mt-1 w-full px-4 py-2.5 rounded-xl bg-white border border-indigo-200 tracking-[0.3em] text-center font-semibold focus:outline-none focus:ring-2 focus:ring-primary-200',
                          'sellerOwnOtp'
                        )}
                        placeholder="6-digit OTP"
                        {...getCreateFieldProps('sellerOwnOtp')}
                      />
                      {renderCreateFieldError('sellerOwnOtp')}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const sellerId = String(form.sellerOwnSellerId || '').trim();
                        if (sellerId) sendSellerOwnDealOtp(sellerId);
                      }}
                      disabled={sellerOwnOtpSending || !form.sellerOwnSellerId}
                      className="px-4 py-2.5 rounded-xl border border-indigo-200 bg-white text-indigo-800 text-sm font-semibold hover:bg-indigo-100 disabled:opacity-60"
                    >
                      {sellerOwnOtpSending ? 'Sending…' : 'Resend OTP'}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 bg-white sticky bottom-0">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50"
                  disabled={createDealLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary px-5 py-2 rounded-xl text-sm disabled:opacity-60"
                  disabled={createDealLoading || sellerOwnOtpSending || !isCreateDealSubmitReady}
                >
                  {createDealLoading || sellerOwnOtpSending
                    ? 'Please wait…'
                    : pendingClearanceDraftId
                      ? 'Create clearance deal'
                      : isPlatformStaffRole && isSellerOwnDealForm && sellerOwnOtpStep !== 'sent'
                        ? 'Send OTP to Seller'
                        : isPlatformStaffRole && isSellerOwnDealForm && sellerOwnOtpStep === 'sent'
                          ? 'Verify OTP & Save Deal'
                          : 'Save Deal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deals;

